import { StepNavigationService } from '@app/services/step-navigation.service';
import { summarizeAiContent, writeAiContent } from './ai-content-writer';
import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { NgIf, NgClass } from '@angular/common';
import { AppConfigService } from 'app/core/config/app-config.service';
import { ImpersonationBannerComponent } from 'app/layout/common/impersonation-banner/impersonation-banner.component';

import { ActivatedRoute, Router, RouterLink, RouterOutlet } from '@angular/router';
import { Subject, takeUntil, interval, distinctUntilChanged, skip } from 'rxjs';
import { FormsModule } from '@angular/forms';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';

// translation
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { FuseDrawerComponent } from '@fuse/components/drawer';
import { FuseConfirmationService } from '@fuse/services/confirmation';

import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { AdminNavigationService } from 'app/core/navigation/admin-navigation.service';

import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { MessagesComponent } from 'app/layout/common/messages/messages.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { QuickChatComponent } from 'app/layout/common/quick-chat/quick-chat.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { ShortcutsComponent } from 'app/layout/common/shortcuts/shortcuts.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { CompanySelectorComponent } from 'app/components/company-selector/company-selector.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

import { DictionaryLookupComponent } from '@app/core/studema/dictionary-lookup/dictionary-lookup.component';

import { AuthRepository, User, UserRole } from '@app/core/auth/auth.repository';
import { environment } from '@env/environment';
import { StudentsService, Student } from '@app/services/students.service';
import { CompaniesService } from '@app/services/companies.service';
import { ChatMockService, ChatMessage, MessageGroup } from '@app/@shared/services/chat-mock.service';
import { GlobalAiChatService, GlobalAiChatConfig } from '@app/services/global-ai-chat.service';
import { SupervisorService } from '@app/services/supervisor.service';
import { MessageCountersService } from '@app/services/message-counters.service';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import { ChatService, ChatMessage as ApiChatMessage } from '@app/services/chat.service';
import { DirectChatService, DirectChatCategoryId, DirectChatRecipient } from '@app/services/direct-chat.service';
import { DirectChatDrawer } from './direct-chat-drawer';
import { ChatFileUploadService, PendingChatAttachment, ChatFileUploadResult } from '@app/services/chat-file-upload.service';
import { FeatureSettingsService } from '@app/services/feature-settings.service';
import { SelfieDialogService } from '@app/services/selfie-dialog.service';
import { VirtualClassroomService } from '@app/services/vclassroom.service';
import { MediaDeviceCoordinatorService } from '@app/services/media-device-coordinator.service';
import { StepAudioCaptureService } from '@app/services/step-audio-capture.service';
import { ChecksheetChangeService } from 'app/modules/admin/academy/changes-academy.service';
import { VoiceInputService } from '@app/@shared/voice-input/voice-input.service';
import { filter } from 'rxjs/operators';
import { WordClickService } from '@app/services/word-click.service';
import { WordSelectionLookupService } from '@app/@shared/services/word-selection-lookup.service';
import { WordLookupPopoverComponent } from '@app/@shared/components/word-lookup-popover/word-lookup-popover.component';
import { IHaveAWinDialogComponent, IHaveAWinDialogData } from './i-have-a-win-dialog.component';
import { NavigationEnd } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { RoomEvent } from 'livekit-client';

@Component({
  selector: 'compact-layout',
  templateUrl: './compact.component.html',
  styleUrls: ['./compact.component.css'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    CommonModule,
    NgClass,
    RouterOutlet,
    RouterLink,
    ImpersonationBannerComponent,
    FormsModule,

    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,

    FuseLoadingBarComponent,
    FuseFullscreenComponent,
    FuseVerticalNavigationComponent,
    FuseDrawerComponent,

    LanguagesComponent,
    SearchComponent,
    ShortcutsComponent,
    MessagesComponent,
    NotificationsComponent,
    UserComponent,

    QuickChatComponent,
    DictionaryLookupComponent,
    CompanySelectorComponent,
    MatDialogModule,
    WordLookupPopoverComponent,

    TranslateModule,
  ],
})
export class CompactLayoutComponent implements OnInit, OnDestroy, AfterViewInit {
  readonly appConfig = inject(AppConfigService);
  private readonly stepNavigation = inject(StepNavigationService);
  private readonly stepAudio = inject(StepAudioCaptureService);
  replyToStep: ChatMessage | null = null;
  chatSendError = '';
  private readonly messageCounters = inject(MessageCountersService);
  private readonly _directChat = inject(DirectChatService);
  private readonly _changeDetectorRef = inject(ChangeDetectorRef);
  // user
  UserRole = UserRole;
  _role: UserRole = UserRole.Student;

  // --
  isScreenSmall: boolean;
  navigation: Navigation;
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  @ViewChild('chatDrawer') chatDrawer: FuseDrawerComponent;
  private _aiChatTeacherElement?: ElementRef<HTMLElement>;
  /**
   * The widget is created by *ngIf (first render, academy re-entry, AI opt-in),
   * so a new element starts without config and without our listeners. It gets
   * its config through the same write path as every later push.
   */
  @ViewChild('aiChatTeacher', { static: false })
  set aiChatTeacherElement(ref: ElementRef<HTMLElement> | undefined) {
    const previous = this._aiChatTeacherElement?.nativeElement;
    this._aiChatTeacherElement = ref;
    if (!ref?.nativeElement || ref.nativeElement === previous) return;
    this.widgetListenersBound = false;
    if (!this.feature_ai) return;
    const element = ref.nativeElement;
    // Called during change detection; write after it.
    queueMicrotask(() => {
      if (this._aiChatTeacherElement?.nativeElement !== element) return;
      const config = this._globalAiChatService.getCurrentConfig();
      if (this.isAcademyContext) {
        this.updateWidgetAttributes(config);
      } else {
        writeAiContent(element, this.buildAiContent(config));
      }
    });
  }
  get aiChatTeacherElement(): ElementRef<HTMLElement> | undefined {
    return this._aiChatTeacherElement;
  }
  @ViewChild('inlineVideoElement', { static: false }) inlineVideoElement?: ElementRef<HTMLVideoElement>;
  @ViewChild('messagesContainer', { static: false }) messagesContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('adminMessagesContainer', { static: false }) adminMessagesContainer?: ElementRef<HTMLDivElement>;
  drawerMode: 'chat' | 'dictionary' = 'chat';
  
  // Inline video in chat sidebar
  hasVideoTrack: boolean = false;
  private inlineVideoTrack: any = null;
  
  // Scroll to bottom button visibility
  showScrollToBottom: boolean = false;
  showScrollToBottomAdmin: boolean = false;
  private inlineAudioTrack: any = null;
  isInlineAudioMuted: boolean = true;
  isInlineVideoHidden: boolean = false;

  // AI Chat feature flags — read from FeatureSettingsService to respect company-level override
  /**
   * AI is active for this student right now: the company feature is on AND the
   * student has not opted out. Everything AI-shaped (widget, mute, mic inject,
   * step config) hangs off this flag.
   */
  feature_ai = false;
  /** Company/system AI feature, regardless of the student's own choice. */
  aiFeatureAvailable = false;
  /** Student turned the AI tutor off — the supervisor validates their steps. */
  aiOptedOut = false;
  /** A save of the opt-out choice is in flight (button disabled meanwhile). */
  aiOptOutSaving = false;

  /**
   * Offer the student the AI on/off switch.
   *
   * Only where there is a choice to make: the company has AI, and the course
   * they are in actually uses it. A course set to no AI shows no switch,
   * because turning it on would change nothing.
   */
  /**
   * Outside a course the widget stays mounted (hidden in the closed drawer)
   * so the Realtime session is up before the student opens one. It connects
   * with `micDeferred`; entering a course only switches the mic on.
   */
  get aiPreconnect(): boolean {
    return this._role === UserRole.Student && this.feature_ai;
  }

  get showAiToggle(): boolean {
    return (
      this._role === UserRole.Student &&
      this.aiFeatureAvailable &&
      !this._globalAiChatService.isCourseAiOff()
    );
  }
  _ai_api_baseurl_chat = this.appConfig.get('ai_api_baseurl_chat');
  ai_chat_debug = this.appConfig.get('ai_chat_debug');
  ai_chat_full_mode = this.appConfig.get('ai_chat_full_mode');

  // AI Chat configuration from service
  aiChatConfig: GlobalAiChatConfig = {
    environment: "webcomponent",
    agentId: 'agent.listener',
    apiBaseUrl: this.appConfig.get('ai_api_baseurl_chat'),
    lang: 'en',
    contentLang: 'en',
    role: (this.appConfig.get('ai_chat_role') as 'child' | 'executive') || 'executive',
    autoStart: true,
    muteAi: true,
    debug: false,
    fullMode: true,
    showWidget: true
  };

  // Track if widget is connected (for mute button state)
  isWidgetConnected: boolean = false;

  // Track previous step_id to detect step changes
  private previousStepId: string | null = null;

  // Track if drawer has been auto-opened (only once per academy visit)
  private hasAutoOpenedDrawer: boolean = false;

  /**
   * Student AI audio may play only while the chat drawer is open.
   * Prevents welcome/resume speech into a closed sidebar.
   */
  private aiChatDrawerReady: boolean = false;

  /** True when a step config was applied muted because the drawer was closed. */
  private pendingMutedSpeak: boolean = false;

  /** Shared mic clone injected into AI widget (classroom-on only). */
  private aiMicInjected = false;
  private aiMicInjectInFlight: Promise<void> | null = null;
  /**
   * When classroom is on, hold autoStart until hub inject finishes (or fails).
   * Prevents the template ai-content bind from connecting with a second GUM.
   */
  private aiMicReadyForConnect = false;
  private widgetListenersBound = false;

  // Route gating: only enable/render/connect AI chat on /academy/*
  isAcademyContext: boolean = false;

  // Students list for admin/supervisor chat
  studentsList: Student[] = [];
  filteredStudentsList: Student[] = [];
  selectedStudent: Student | null = null;
  isInStudentChat: boolean = false;
  searchTerm: string = '';

  // Staff direct chat (role switch -> recipient -> private 1:1 thread); null for students
  @ViewChild('directMessagesContainer', { static: false }) directMessagesContainer?: ElementRef<HTMLDivElement>;
  directChat: DirectChatDrawer | null = null;
  
  // Unread message counts for each student (studentId -> count)
  unreadMessageCounts: { [key: number]: number } = {};
  unreadNotificationCounts: { [key: number]: number } = {};

  // Chat pagination (supervisor view)
  chatPage = 1;
  chatPerPage = 20;
  chatTotalMessages = 0;
  chatHasMore = false;
  chatIsLoadingMore = false;

  // Map student_id -> student_course_id for "Review" navigation (first course with pending steps)
  private supervisorCourseMap: { [studentId: number]: number } = {};
  // Map student_id -> pending steps count (from getSupervisorStudents, matches supervisor activity list)
  private supervisorPendingCountMap: { [studentId: number]: number } = {};

  // show the vertical navigation
  showVerticalNavigation: boolean = false;
  _client: string = 'studema';
  _featuresTopNav: boolean = false;
  _branding: string = 'Studema Online Training';
  _logo_white: string = 'client-logo-white.png'; // inverse/light logo — for dark nav
  _logo: string       = 'client-logo.png';        // regular/dark logo  — for light nav
  _feature_classroom: boolean = false;
  feature_header_white_bg: boolean = false;
  
  // Independent feature flags for top nav buttons
  feature_game: boolean = false;
  feature_clearword: boolean = false;
  feature_chat_supervisor: boolean = false;
  /** Company feature: show "I have a win" in top nav for students */
  feature_ihaveawin: boolean = false;
  feature_help: boolean = true;

  isMobile: boolean = false;
  isTablet: boolean = false;
  isDesktop: boolean = false;

  // Chat badge count (from environment; 0 hides badge)
  chatBadgeCount: number = this.appConfig.get('chat_badge_count') ?? 0;
  
  // Total unread message count for supervisor (all students)
  totalUnreadCount: number = 0;
  
  // Separate counts for chat messages (green) vs notifications (orange)
  totalUnreadMessages: number = 0;
  totalUnreadNotifications: number = 0;
  totalWaitingForStudent: number = 0;
  /** Pending steps awaiting supervisor review (status_id=2), company-scoped */
  totalPendingStepsCount: number = 0;

  // Company-level ACK setting: if true, students must click OK to mark messages read
  companyAckEnabled: boolean = false;

  // Dynamic nav-bar logo URL from company branding (brand_logo_dark for white bar, brand_logo_light for dark bar)
  navLogoUrl: string = '';

  // Chat inputs
  chatInput: string = '';
  adminChatInput: string = '';

  // Displayed chat messages (for mock)
  chatMessages: ChatMessage[] = [];
  // Messages grouped by step
  groupedMessages: MessageGroup[] = [];
  // Current step context (for scrolling to the right group)
  currentStepId: number | null = null;
  // Supervisor info captured from incoming messages (student view)
  supervisorInfo: { name: string; avatar: string | null } | null = null;
  // Twin info captured from incoming messages (supervisor view — twinning active)
  twinInfo: { name: string; avatar: string | null } | null = null;
  // Optional pending image (from paste) — kept for backwards-compat render path
  pastedImageUrl: string | null = null;

  // Pending attachments for real file upload (chat sidebar)
  pendingChatAttachments: PendingChatAttachment[] = [];

  // Lightbox state
  lightboxOpen: boolean = false;
  lightboxImages: { url: string; name: string }[] = [];
  lightboxIndex: number = 0;

  /**
   * Constructor
   */
  constructor(
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private _navigationService: NavigationService,
    private _fuseMediaWatcherService: FuseMediaWatcherService,
    private _fuseNavigationService: FuseNavigationService,
    private _authRepo: AuthRepository,
    private _adminNavigationService: AdminNavigationService,
    private _studentsService: StudentsService,
    private _chatMock: ChatMockService,
    private _globalAiChatService: GlobalAiChatService,
    private _supervisorService: SupervisorService,
    private _chatService: ChatService,
    private _selfieDialogService: SelfieDialogService,
    private _virtualClassroomService: VirtualClassroomService,
    private _mediaHub: MediaDeviceCoordinatorService,
    private _checksheetChangeService: ChecksheetChangeService,
    private _voiceInputService: VoiceInputService,
    private http: HttpClient,
    private _dialog: MatDialog,
    private _companiesService: CompaniesService,
    private _notificationsService: NotificationsService,
    private _featureSettings: FeatureSettingsService,
    private _translateService: TranslateService,
    private _chatFileService: ChatFileUploadService,
    private _wordClickService: WordClickService,
    private _wordSelectionService: WordSelectionLookupService,
    private _fuseConfirmationService: FuseConfirmationService,
  ) {
    // set the vertical navigation based on admin role
    this._client = this.appConfig.get('client');
    this._branding = this.appConfig.get('branding');
    this._logo_white = this.appConfig.get('logo_invers');
    this._logo       = this.appConfig.get('logo');

    this._feature_classroom = this._featureSettings.isFeatureEnabled('feature_classroom');
    this.aiFeatureAvailable = this._featureSettings.isFeatureEnabled('feature_ai');
    this.aiOptedOut = this._globalAiChatService.isOptedOut();
    // The course AI mode only arrives with the enrollment, well after this
    // constructor runs, so the flag is followed rather than read once.
    this._globalAiChatService.refreshAiActive();
    this._globalAiChatService.aiActive$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((active) => {
        if (this.feature_ai === active) return;
        this.feature_ai = active;
        // The course's AI mode arrives after the route: entering /academy/*
        // saw AI off and skipped the mic resume, so the widget stayed deaf.
        if (active && this.isAcademyContext && this._globalAiChatService.isCourseAiOn()) {
          void this.ensureSharedAiMic().then(() => this.resumeWidgetOwnedMic());
        }
        // A course with AI off shows the supervisor chat, which needs its
        // history the same way opting out does.
        if (!active && this._role === UserRole.Student) {
          this.loadChatMessages();
        }
        // *ngIf destroys the element; a re-created one must be bound again.
        if (!active) {
          this.widgetListenersBound = false;
          this.isWidgetConnected = false;
        }
        this._changeDetectorRef.markForCheck();
      });
    // Only now is it known whether this course runs AI; the drawer waits for it.
    this._globalAiChatService.courseAiSettings$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => {
        if (this.isAcademyContext) {
          if (this._globalAiChatService.isCourseAiOn()) {
            // Switching from a No-AI course re-creates the widget via *ngIf on
            // this tick; arm it once Angular has put it back in the DOM.
            setTimeout(() => {
              if (this.isAcademyContext && this._globalAiChatService.isCourseAiOn()) {
                this.armAcademyAiSession();
              }
            }, 100);
          } else {
            this.releaseSharedAiMic();
            this.releaseWidgetOwnedMic('course-ai-off');
          }
        }
        setTimeout(() => this.openAiChatDrawerIfNeeded('course-ai-settings'), 100);
      });
    this.feature_header_white_bg = this._featureSettings.isFeatureEnabled('feature_header_white_bg');

    // get current user and userrole
    this._role = this._authRepo.getUser().role;

    this._featuresTopNav = this._featureSettings.isFeatureEnabled('featuresTopNav');
    
    // Independent feature flags for top nav buttons
    this.feature_game         = this._featureSettings.isFeatureEnabled('feature_game');
    this.feature_clearword    = this._featureSettings.isFeatureEnabled('feature_clearword');
    if (this.feature_clearword) {
      this._wordSelectionService.enable();
    }
    this.feature_chat_supervisor = this._featureSettings.isFeatureEnabled('feature_chat_supervisor');
    this.feature_ihaveawin = this._featureSettings.isFeatureEnabled('feature_ihaveawin');

    // Update sidebar visibility based on admin role
    this.updateSidebarVisibility();

    // Validate apiBaseUrl on construction (only if feature_ai is enabled)
    if (this.aiFeatureAvailable && !this.appConfig.get('ai_api_baseurl_chat')) {
      console.error('❌ ai_api_baseurl_chat is not set in environment.ts');
    }

    this._fuseMediaWatcherService.onMediaChange$.subscribe(({ matchingAliases }) => {
      //console.log(matchingAliases);
      this.isMobile =
        (matchingAliases.includes('sm') || matchingAliases.includes('xs')) &&
        !(
          matchingAliases.includes('md') ||
          matchingAliases.includes('lg') ||
          matchingAliases.includes('xl') ||
          matchingAliases.includes('2xl')
        );
      //this.isTablet = matchingAliases.includes('md');
      //this.isDesktop = matchingAliases.some((alias) => ['lg', 'xl', '2xl'].includes(alias));
      this.isDesktop = !this.isMobile;
    });
  }

  /** Debug helper for sidebar chat message load/convert. Off for now — noise. */
  private _lifecycleLog(_label: string, _payload?: any, _color: string = '#0ea5e9'): void {
    // no-op (re-enable console.log here when debugging chat lifecycle)
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   *
   * check if the current user / has the necessary role to be SuperUser
   * @readonly
   * @type {boolean}
   * @memberof CompactLayoutComponent
   */
  get hasSUPermission(): boolean {
    if (
      this._role == UserRole.CourseAdmin ||
      this._role == UserRole.Supervisor ||
      this._role == UserRole.SuperUser ||
      this._role == UserRole.SysAdmin
    )
      return true;
    else return false;
  }

  /** Roles that get the live message counters (top-nav badges + 30s poll). */
  private get _hasCounterAccess(): boolean {
    return (
      this._role === UserRole.CourseAdmin ||
      this._role === UserRole.Supervisor ||
      this._role === UserRole.SuperUser ||
      this._role === UserRole.SysAdmin
    );
  }

  /**
   * Check if user has admin role and should see admin sidebar
   */
  get hasAdminRole(): boolean {
    return this._adminNavigationService.hasAdminRole();
  }

  /**
   * Get admin role name for display
   */
  get adminRoleName(): string {
    return this._adminNavigationService.getAdminRoleName(this._role);
  }

  /**
   * Getter for current year
   */
  get currentYear(): number {
    return new Date().getFullYear();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    this.stepNavigation.openConversation$.pipe(takeUntil(this._unsubscribeAll)).subscribe(studentId => {
      if (this._role === UserRole.Student) return;
      this._studentsService.getStudentById(studentId).pipe(takeUntil(this._unsubscribeAll))
        .subscribe(student => this.openStudentChat(student));
    });
    // Hub A/V upgrade stops AI mic clones — re-inject so Realtime STT stays live.
    this._mediaHub
      .getLeasesInvalidated()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((ids) => {
        if (!ids?.includes('ai-chat')) return;
        if (!this.feature_ai || !this._feature_classroom || !this.isAcademyContext) {
          return;
        }
        console.warn('[StepAI] MediaHub invalidated ai-chat mic lease — re-injecting');
        this.aiMicInjected = false;
        void this.ensureSharedAiMic(true);
      });

    // Open dictionary drawer and pre-fill word whenever a step-content word is clicked.
    this._wordClickService.word$
      .pipe(filter(w => !!w), takeUntil(this._unsubscribeAll))
      .subscribe(() => {
        this.drawerMode = 'dictionary';
        this.chatDrawer.open();
      });

    // Load company ACK setting (students must click OK to mark messages read)
    this._featureSettings.companySettings$.pipe(takeUntil(this._unsubscribeAll)).subscribe(settings => {
      this.companyAckEnabled = settings?.messaging?.ack_required !== false; // default ON
      // Re-evaluate feature flags that can be overridden per company
      this.feature_help = this._featureSettings.isFeatureEnabled('feature_help');
      this.feature_game = this._featureSettings.isFeatureEnabled('feature_game');
      // Nav bar colour: branding.white_navigation is the single source of truth
      this.feature_header_white_bg = !!settings?.branding?.white_navigation;

      // Resolve dynamic nav logo: white bar → brand_logo_dark; dark bar → brand_logo_light
      const branding = settings?.branding;
      const mediaId = this.feature_header_white_bg
        ? (branding?.brand_logo_dark  || '')
        : (branding?.brand_logo_light || '');
      if (mediaId) {
        const token     = this._authRepo.getAccessToken();
        const serverUrl = this.appConfig.get('serverUrl');
        this.navLogoUrl = `${serverUrl}/v1/company-media/${mediaId}?token=${token}`;
      } else {
        this.navLogoUrl = '';
      }
    });

    // Subscribe to navigation data
    this._navigationService.navigation$.pipe(takeUntil(this._unsubscribeAll)).subscribe((navigation: Navigation) => {
      this.navigation = navigation;
      // Update navigation items with badge for Messages item
      this.updateNavigationBadge();
    });
    
    // Load total unread count for supervisor / admin roles
    if (this._hasCounterAccess) {
      this.loadTotalUnreadCount();
      this.buildSupervisorCourseMap(); // Also refresh pending counts (matches supervisor activity list)
      // Poll for updates every 30 seconds (counters + pending map for live updates)
      interval(30000)
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe(() => {
          this.loadTotalUnreadCount();
          this.buildSupervisorCourseMap();
          // If admin/supervisor is actively viewing a student chat and is at the bottom,
          // reload messages so new incoming messages appear automatically.
          if (this.isInStudentChat && this.selectedStudent && !this.showScrollToBottomAdmin) {
            this.loadStudentChatMessages(this.selectedStudent.t_ot_studentid);
          }
        });

      this._supervisorService.pendingSteps$
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe((summary) => {
          this.totalPendingStepsCount = summary.total;
          this.supervisorPendingCountMap = { ...summary.byStudent };
          this.supervisorCourseMap = { ...summary.firstPendingCourseByStudent };
          this.updateNavigationBadge();
        });

      // Subscribe to the shared counters cache so that whenever ANY consumer
      // (e.g. the /supervisor student list) fetches fresh counters, all UI surfaces
      // (top-nav badge, chat-drawer per-student badges) are automatically updated
      // from the same data — preventing stale-total vs per-student count mismatches.
      this._supervisorService.counters$
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe((counters) => {
          this.totalUnreadMessages = Number(counters?.totals?.messages_unread || 0);
          this.chatBadgeCount = this.totalUnreadMessages;
          this.totalWaitingForStudent = Number(counters?.totals?.waiting_for_student || 0);
          this._refreshCompanyScopedNotificationCount();

          // Also refresh per-student maps used by the chat drawer.
          // Only overwrite counts that are not locally zeroed (open-chat auto-clear).
          Object.entries(counters?.by_student || {}).forEach(([studentIdStr, row]) => {
            const sid = Number(studentIdStr);
            if (!sid) return;
            // Respect a locally-cleared count: if we already zeroed it
            // (supervisor opened the chat), don't re-inflate until next real poll.
            const freshMsgs = Number((row as any)?.messages_unread || 0);
            const freshNotifs = Number((row as any)?.notifications_needs_action || 0);
            // Only update if the value is higher than what we have locally — avoids
            // re-showing a badge the user just cleared before the next server poll.
            if (freshMsgs > (this.unreadMessageCounts[sid] || 0)) {
              this.unreadMessageCounts[sid] = freshMsgs;
            }
            if (freshNotifs > (this.unreadNotificationCounts[sid] || 0)) {
              this.unreadNotificationCounts[sid] = freshNotifs;
            }
          });
        });
    }
    
    // Load total unread count for students (from current course if available)
    if (this._role === UserRole.Student) {
      // Listen to route changes to get student_course_id
      this._router.events
        .pipe(
          filter(event => event instanceof NavigationEnd),
          takeUntil(this._unsubscribeAll)
        )
        .subscribe((event: NavigationEnd) => {
          this.loadStudentUnreadCount();
        });
      
      // Load initially
      this.loadStudentUnreadCount();
      
      // Poll while the student application is visible.
      interval(5000)
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe(() => {
          this.loadStudentUnreadCount();
          // Quiet refresh while the drawer is open. Does not clear the list
          // or move the scroll unless a new message arrived at the bottom.
          if (this.chatDrawer?.opened && this.drawerMode === 'chat') {
            this.refreshChatMessages();
          }
        });
    }

    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$.pipe(takeUntil(this._unsubscribeAll)).subscribe(({ matchingAliases }) => {
      // Check if the screen is small
      this.isScreenSmall = !matchingAliases.includes('md');
    });

    // Subscribe to admin navigation service changes
    this._adminNavigationService.showAdminSidebar$.pipe(takeUntil(this._unsubscribeAll)).subscribe((showSidebar) => {
      this.showVerticalNavigation = showSidebar;
    });

    // Load students list when component initializes
    this.loadStudentsList();
    this._initDirectChat();

    // Reload students list when company changes
    this._companiesService.selectedCompanyId$
      .pipe(
        takeUntil(this._unsubscribeAll),
        distinctUntilChanged(),
        skip(1) // Skip initial emission
      )
      .subscribe(() => {
        this.loadStudentsList();
        this._refreshCompanyScopedNotificationCount();
        this.buildSupervisorCourseMap();
      });

    // Load chat history from storage (per user) - but don't seed mock messages
    // Real messages will be loaded from API when chat opens
    this.loadMessagesFromStorage();

    // Subscribe to global AI chat config changes (only if feature_ai is enabled).
    // Bound to the company flag, not the student's choice, so a student who
    // turns the AI back on mid-session still gets config pushes.
    if (this.aiFeatureAvailable) {
      this._globalAiChatService.config$
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe((config: GlobalAiChatConfig) => {
          this.aiChatConfig = config;
          
          if (this.isAcademyContext && this.aiChatTeacherElement?.nativeElement) {
            this.updateWidgetAttributes(config);
            
            // IMMEDIATE MUTE: If switching to listener, mute immediately
            if (config.agentId === 'agent.listener' && config.muteAi) {
              this.muteWidgetImmediately();
            }
          } else if (this.feature_ai && this.aiChatTeacherElement?.nativeElement) {
            // Hidden pre-connected widget outside a course: config only, no mic.
            writeAiContent(this.aiChatTeacherElement.nativeElement, this.buildAiContent(config));
          }
        });
    }

    // Listen to route changes to detect step type and step_id changes
    // Initialize academy context for first load
    this.isAcademyContext = (this._router.url || '').startsWith('/academy/');

    this._router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe((event: NavigationEnd) => {
        const url = event.urlAfterRedirects;

        // Route gating: we only want the drawer + ai webcomponent on /academy/*
        const wasAcademy = this.isAcademyContext;
        this.isAcademyContext = url.startsWith('/academy/');

        // If we left /academy/*, close drawer and drop the mic; the widget stays
        // mounted (hidden) with micDeferred so the session is ready for the next course
        if (wasAcademy && !this.isAcademyContext) {
          try {
            if (this.chatDrawer?.opened) {
              this.chatDrawer.close();
            }
          } catch {
            // ignore
          }

          this.markAiChatDrawerClosed('left-academy');
          this.releaseSharedAiMic();
          this.releaseWidgetOwnedMic('left-academy');
          this.releaseClassroomMedia();
          this.widgetListenersBound = false;

          // Unconditionally: a course with AI off leaves `courseAiMode` at
          // 'off', and the next course would then enter with AI still off.
          this._globalAiChatService.resetAcademyVisit();
          if (this.feature_ai) {
            this._globalAiChatService.resetToListener();
            this._globalAiChatService.updateConfig({ showWidget: false });
          }

          // allow auto-open again the next time we enter /academy/*
          this.hasAutoOpenedDrawer = false;
        }

        // If we entered /academy/*, allow widget rendering and open chat once
        if (!wasAcademy && this.isAcademyContext && this.feature_ai) {
          this._globalAiChatService.updateConfig({ showWidget: true });
          this.resumeClassroomMedia();
          // The course's AI mode usually arrives later; courseAiSettings$ arms it then.
          if (this._globalAiChatService.isCourseAiOn()) {
            this.armAcademyAiSession();
          } else {
            this.setupWidgetEventListeners();
            this.updateWidgetAttributes(this._globalAiChatService.getCurrentConfig());
          }
          // Clear sticky manual-disconnect so autoStart can reconnect.
          try {
            window.dispatchEvent(new CustomEvent('ai-chat-allow-reconnect'));
          } catch {
            /* ignore */
          }
          // Drawer ViewChild / *ngIf widget may not be ready on this tick
          setTimeout(() => this.openAiChatDrawerIfNeeded('enter-academy'), 100);
        }
        
        // Extract step_id from URL (format: step_id=123 or in route params)
        // Try to get from router state first, then fallback to URL parsing
        let currentStepId: string | null = null;
        
        // Check router state for step_id in child routes (secondary outlet)
        const routerState = this._router.routerState;
        const rootRoute = routerState.root;
        
        // Traverse child routes to find step_id param
        let childRoute = rootRoute.firstChild;
        while (childRoute) {
          // Check for secondary outlet
          const secondaryOutlet = childRoute.children.find(
            child => child.outlet === 'secondary'
          );
          
          if (secondaryOutlet) {
            const stepIdParam = secondaryOutlet.snapshot.paramMap.get('step_id');
            if (stepIdParam) {
              currentStepId = stepIdParam;
              break;
            }
          }
          
          // Also check current route params
          const stepIdInRoute = childRoute.snapshot.paramMap.get('step_id');
          if (stepIdInRoute) {
            currentStepId = stepIdInRoute;
            break;
          }
          
          childRoute = childRoute.firstChild;
        }
        
        // Fallback: Parse from URL if not found in route
        if (!currentStepId) {
          const stepIdMatch = url.match(/step_id[=:](\d+)/i);
          if (stepIdMatch) {
            currentStepId = stepIdMatch[1];
          }
        }
        
        // On step_id change: clear previous agent+command immediately so a
        // stale Gallery/read-examples config cannot re-speak while the next
        // step loads. The new step then pushes its own agentId+content.
        if (currentStepId && this.feature_ai) {
          this._globalAiChatService.setActiveStepId(currentStepId);
          if (this.previousStepId !== null && this.previousStepId !== currentStepId) {
            console.log('[StepAI] step changed — clear previous AI config', {
              previousStepId: this.previousStepId,
              newStepId: currentStepId,
              url: url
            });
            this._globalAiChatService.notifyStepIdChanged(
              this.previousStepId,
              currentStepId
            );
          }
          this.previousStepId = currentStepId;
        } else if (!currentStepId) {
          this.previousStepId = null;
          this._globalAiChatService.setActiveStepId(null);
        }
        
        // Detect step type from URL — must include quiz/course-start so we do
        // not race their updateConfig with a resetToListener hop on reload.
        if (url.includes('/clear-word') || url.includes('/read-text') ||
            url.includes('/gallery') || url.includes('/do-drill') ||
            url.includes('/write-essay') || url.includes('/quiz') ||
            url.includes('/course-start')) {
          // Step components push their own agentId + content (+ flowSeq)
          console.log('[StepAI] Detected AI-capable step route:', url);
        } else if (this.isAcademyContext && this.feature_ai) {
          // Still in academy but not an AI step route — idle listener.
          // Skip while course-entry welcome is speaking (e.g. FinalTest land).
          if (!this._globalAiChatService.isCourseEntryWelcomeActive()) {
            this._globalAiChatService.resetToListener();
          }
        }
      });
  }

  /**
   * After view init
   */
  ngAfterViewInit(): void {
    // Set initial attributes on AI chat component if feature_ai is enabled
    // Only do this inside /academy/* so /home never triggers a connect
    if (this.feature_ai && this.isAcademyContext && this.aiChatTeacherElement?.nativeElement) {
      // Listeners first — do not miss resume_terminal while mic inject is in flight.
      this.setupWidgetEventListeners();
      if (this._globalAiChatService.isCourseAiOn()) {
        this.armAcademyAiSession();
      }
    }
    
    // Direct land on /academy/* — open chat once (also covered by NavigationEnd enter)
    setTimeout(() => this.openAiChatDrawerIfNeeded('after-view-init'), 100);
  }

  /**
   * Course is known to run AI: inject the shared mic BEFORE the autoStart
   * connect (template bind holds autoStart), then re-arm the widget's capture.
   */
  private armAcademyAiSession(): void {
    void this.ensureSharedAiMic().then(() => {
      this.setupWidgetEventListeners();
      this.updateWidgetAttributes(this._globalAiChatService.getCurrentConfig());
      this.resumeWidgetOwnedMic();
    });
  }

  /** Classroom-off: always. Classroom-on: only after hub inject attempt finishes. */
  private canAutoStartAiChat(): boolean {
    // Inside a course, connect only once the enrollment says the course runs AI.
    if (this.isAcademyContext && !this._globalAiChatService.isCourseAiOn()) return false;
    if (!this._feature_classroom || !this.isAcademyContext) return true;
    return this.aiMicReadyForConnect;
  }

  /** Mute AI audio until the student can see the chat drawer. */
  private effectiveMuteAi(configMuteAi: boolean | undefined): boolean {
    if (configMuteAi) return true;
    if (this.feature_ai && this.isAcademyContext && !this.aiChatDrawerReady) {
      return true;
    }
    return !!configMuteAi;
  }

  /**
   * Auto-open the student AI chat drawer once per academy visit.
   * Does not reopen if the student closed it during this visit.
   */
  private openAiChatDrawerIfNeeded(reason: string): void {
    if (
      !this.feature_ai ||
      !this._globalAiChatService.isCourseAiOn() ||
      !this.isAcademyContext ||
      this._role !== UserRole.Student ||
      this.hasAutoOpenedDrawer ||
      !this.chatDrawer
    ) {
      return;
    }

    try {
      this.drawerMode = 'chat';
      if (!this.chatDrawer.opened) {
        this.chatDrawer.open();
      }
      this.hasAutoOpenedDrawer = true;
      this._selfieDialogService.setChatOpen(true);
      this.loadChatMessages();
      setTimeout(() => this.attachVideoToInlineElement(), 200);
      this.releaseAiChatDrawerAndSpeak(reason);
      console.log('🚀 Auto-opened chat drawer', {
        reason,
        companyAckEnabled: this.companyAckEnabled,
        messaging: this._featureSettings.getCompanySettings()?.messaging,
      });
    } catch (e) {
      console.warn('[StepAI] auto-open chat drawer failed', reason, e);
    }
  }

  /**
   * Drawer is visible — allow unmute. Only re-push (new flowSeq) when a step
   * config was previously applied muted; otherwise a re-push cutovers mid-speech.
   */
  private releaseAiChatDrawerAndSpeak(reason: string): void {
    const hadMutedPreload = this.pendingMutedSpeak;
    this.aiChatDrawerReady = true;
    this.pendingMutedSpeak = false;
    if (!this.feature_ai || !this.isAcademyContext) return;

    const cfg = this._globalAiChatService.getCurrentConfig();
    const agentId = cfg.agentId || 'agent.listener';

    if (
      hadMutedPreload &&
      agentId !== 'agent.listener' &&
      cfg.muteAi === false
    ) {
      // Bump flowSeq so orchestrator accepts a fresh audible speak
      this._globalAiChatService.updateConfig({
        agentId,
        content: cfg.content,
        muteAi: false,
        role: cfg.role,
        showWidget: true,
        sourceStepId: this._globalAiChatService.getConfigStepId(),
      });
      console.log('[StepAI] drawer ready — re-pushed after muted preload', {
        reason,
        agentId,
      });
      return;
    }

    if (this.aiChatTeacherElement?.nativeElement) {
      this.updateWidgetAttributes(cfg);
    }
  }

  /** Drawer closed or left academy — mute AI again. */
  private markAiChatDrawerClosed(reason: string): void {
    if (!this.aiChatDrawerReady && reason !== 'left-academy') return;
    this.aiChatDrawerReady = false;
    this.pendingMutedSpeak = false;
    if (this.feature_ai) {
      this.muteWidgetImmediately();
    }
  }

  /**
   * Setup event listeners for widget status
   */
  private setupWidgetEventListeners(): void {
    if (!this.aiChatTeacherElement?.nativeElement) return;
    if (this.widgetListenersBound) return;
    this.widgetListenersBound = true;
    
    const element = this.aiChatTeacherElement.nativeElement as any;
    
    // Listen to connection status changes
    element.addEventListener('ai-chat-status', (event: CustomEvent) => {
      const status = event.detail.status;
      this.isWidgetConnected = status === 'CONNECTED';
      console.log('📡 Widget status:', status);
      if (status === 'DISCONNECTED') {
        this._globalAiChatService.reportAiSessionUnavailable(`status:${status}`);
      }
    });
    
    // Listen to agent changes — unmute helpers/workers; remute quiet listener
    element.addEventListener('ai-chat-agent-changed', (event: CustomEvent) => {
      const agentName = String(event.detail?.agentName || '');
      console.log('🔄 Widget agent changed:', agentName);
      const isListener =
        !agentName ||
        agentName === 'listenerAgent' ||
        agentName === 'agent.listener';
      if (!isListener) {
        this.applyMuteStateImmediately(false, element);
      } else if (this.aiChatConfig?.muteAi) {
        this.applyMuteStateImmediately(true, element);
      }
    });
    
    // Listen to mute state changes
    element.addEventListener('ai-chat-mute', (event: CustomEvent) => {
      console.log('🔇 Widget mute state:', event.detail.muted);
    });

    // Orchestrator completion gate → enable Done on AI steps
    element.addEventListener('ai-chat-flow-terminal', (event: CustomEvent) => {
      const detail = event?.detail || {};
      console.log('[StepAI] ai-chat-flow-terminal', {
        flowId: detail.flowId,
        reason: detail.reason,
        readyForDone: detail.readyForDone === true,
        stepVisitId: detail.stepVisitId,
      });
      // Cutover tear-down is not a new-step decision — do not re-lock Done.
      if (detail.reason === 'replaced_by_host_event') {
        return;
      }
      this._globalAiChatService.setReadyForDone(detail.readyForDone === true, {
        ...detail,
        flowId: detail.flowId,
        reason: detail.reason,
      });
    });

    // Mid-flow Done unlock (e.g. reading wait before terminal)
    element.addEventListener('ai-chat-ready-for-done', (event: CustomEvent) => {
      const detail = event?.detail || {};
      console.log('[StepAI] ai-chat-ready-for-done', {
        flowId: detail.flowId,
        readyForDone: detail.readyForDone === true,
        stepVisitId: detail.stepVisitId,
      });
      this._globalAiChatService.setReadyForDone(detail.readyForDone === true, {
        ...detail,
        flowId: detail.flowId,
        reason: 'readyForDone',
      });
    });

    element.addEventListener('ai-chat-step-timing', (event: CustomEvent) => {
      this._globalAiChatService.reportStepTiming(event?.detail);
    });

    // Dispatched from inside the widget's flow start; replay after it returns.
    element.addEventListener('ai-chat-step-accepted', (event: CustomEvent) => {
      const detail = event?.detail;
      queueMicrotask(() => this._globalAiChatService.onStepAccepted(detail));
    });

    // Agent → host messages (e.g. contactSupervisorAgent sendMessage)
    element.addEventListener('ai-chat-message', (event: CustomEvent) => {
      const detail = event?.detail || {};
      const messageType = String(detail.messageType || '');
      console.log('[StepAI] ai-chat-message', {
        messageType,
        agentName: detail.agentName,
        payloadKeys: detail.payload ? Object.keys(detail.payload) : [],
      });
      if (messageType === 'callSupervisor') {
        this.callSupervisor();
      }
    });

    // Phase 2 hardening: surface widget errors (mic, WebRTC, session auth, …)
    element.addEventListener('ai-chat-error', (event: CustomEvent) => {
      const detail = event?.detail || {};
      console.error('[StepAI] ai-chat-error', {
        code: detail.code,
        message: detail.message,
        recoverable: detail.recoverable,
      });
      // A mandatory gate must not turn an unrecoverable widget fault into a
      // step the student can never leave.
      if (detail.recoverable !== true) {
        this._globalAiChatService.reportAiSessionUnavailable(
          `error:${detail.code || 'unknown'}`
        );
      }
    });

    // The widget hands back the student's microphone audio when the course
    // opted into recording. Angular owns the upload: it holds the token.
    // Position inside a multi-item step (which gallery slide is showing).
    // Deliberately an event and not a config push: a config push restarts
    // the AI flow, and turning a page is not a new step.
    this._globalAiChatService.stepProgress$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((progress) => {
        element.dispatchEvent(
          new CustomEvent('ai-chat-step-progress', {
            detail: progress,
            bubbles: false,
          })
        );
      });

    // The AI turning the page for the student after a good repeat.
    element.addEventListener('ai-chat-request-slide', (event: CustomEvent) => {
      const detail = event?.detail || {};
      this._globalAiChatService.requestSlide({
        index: typeof detail.index === 'number' ? detail.index : undefined,
        direction: detail.direction === 'prev' ? 'prev' : 'next',
      });
    });
  }

  /**
   * When classroom media is on: acquire shared A/V once and inject a mic clone
   * into the AI widget before it connects. Classroom-off: no-op (widget owns mic).
   */
  private async ensureSharedAiMic(force = false): Promise<void> {
    if (!this.feature_ai || !this._feature_classroom || !this.isAcademyContext ||
        !this._globalAiChatService.isCourseAiOn()) {
      this.aiMicReadyForConnect = true;
      try {
        (window as any).__aiChatExpectSharedMic = false;
      } catch {
        /* ignore */
      }
      return;
    }

    const injectedMicLive = (): boolean => {
      const existing = (window as any).__aiChatInputMediaStream as MediaStream | null;
      return !!existing
        ?.getAudioTracks?.()
        ?.some((t: MediaStreamTrack) => t.readyState === 'live');
    };

    // Wait for any in-flight inject first. Do NOT return early on force —
    // hub A/V upgrades often invalidate the clone while the first inject
    // is still finishing (leasesInvalidated during aiMicInjectInFlight).
    if (this.aiMicInjectInFlight) {
      await this.aiMicInjectInFlight;
    }

    if (this.aiMicInjected && !force && injectedMicLive()) {
      this.aiMicReadyForConnect = true;
      return;
    }

    if (this.aiMicInjected && !injectedMicLive()) {
      console.warn('[StepAI] Injected AI mic track dead — re-injecting');
    }

    // Another caller may have started inject while we awaited.
    if (this.aiMicInjectInFlight) {
      await this.aiMicInjectInFlight;
      if (!force && this.aiMicInjected && injectedMicLive()) {
        this.aiMicReadyForConnect = true;
        return;
      }
    }

    this.aiMicInjected = false;
    this.aiMicReadyForConnect = false;
    try {
      (window as any).__aiChatExpectSharedMic = true;
    } catch {
      /* ignore */
    }

    this.aiMicInjectInFlight = (async () => {
      const deliverMic = (stream: MediaStream, mode: 'av' | 'audio'): void => {
        const element = this.aiChatTeacherElement?.nativeElement as any;
        if (element && typeof element.setInputMediaStream === 'function') {
          element.setInputMediaStream(stream);
        } else {
          (window as any).__aiChatInputMediaStream = stream;
        }
        // Post-connect: rebound outbound sender even if setInputMediaStream
        // raced before realtime API was published.
        try {
          const rt = (window as any).__aiChatRealtimeSession?.realtime;
          if (rt && typeof rt.replaceMicStream === 'function') {
            void rt.replaceMicStream(stream).catch((err: unknown) => {
              console.warn('[StepAI] replaceMicStream after inject failed', err);
            });
          }
        } catch {
          /* ignore */
        }
        this.aiMicInjected = true;
        console.log(
          `🎤 [StepAI] Shared mic clone injected for AI (classroom on, ${mode})`
        );
      };

      const injectMic = async (mode: 'av' | 'audio'): Promise<void> => {
        if (mode === 'av') {
          // Prefer A/V once so classroom join reuses the same capture.
          await this._mediaHub.ensureCapture({ video: true, audio: true });
        } else {
          await this._mediaHub.ensureCapture({ video: false, audio: true });
        }
        const stream = await this._mediaHub.checkoutMicStream('ai-chat');
        deliverMic(stream, mode);
      };

      try {
        try {
          await injectMic('av');
        } catch (avErr) {
          console.warn(
            '[StepAI] A/V hub capture failed — retrying audio-only for AI mic',
            avErr
          );
          await injectMic('audio');
        }
        // If a hub upgrade killed the clone mid-inject, checkout once more.
        if (!injectedMicLive()) {
          console.warn(
            '[StepAI] Injected mic died during inject — re-checkout after hub settle'
          );
          await this._mediaHub.ensureCapture({ video: true, audio: true });
          const stream = await this._mediaHub.checkoutMicStream('ai-chat');
          deliverMic(stream, 'av');
        }
      } catch (err) {
        console.warn(
          '[StepAI] Shared mic inject failed — AI will use its own getUserMedia',
          err
        );
        try {
          (window as any).__aiChatExpectSharedMic = false;
        } catch {
          /* ignore */
        }
      } finally {
        this.aiMicReadyForConnect = true;
        this.aiMicInjectInFlight = null;
      }
    })();

    await this.aiMicInjectInFlight;
  }

  /** Release AI mic lease and clear injected stream (classroom-off / leave academy). */
  private releaseSharedAiMic(): void {
    if (!this.aiMicInjected && !(window as any).__aiChatInputMediaStream) {
      this.aiMicReadyForConnect = false;
      try {
        (window as any).__aiChatExpectSharedMic = false;
      } catch {
        /* ignore */
      }
      return;
    }
    try {
      const element = this.aiChatTeacherElement?.nativeElement as any;
      if (element && typeof element.setInputMediaStream === 'function') {
        element.setInputMediaStream(null);
      }
    } catch {
      /* ignore */
    }
    (window as any).__aiChatInputMediaStream = null;
    try {
      (window as any).__aiChatExpectSharedMic = false;
    } catch {
      /* ignore */
    }
    this._mediaHub.release('ai-chat');
    this.aiMicInjected = false;
    this.aiMicReadyForConnect = false;
    console.log('🎤 [StepAI] Shared AI mic lease released');
  }

  /**
   * Stop the capture the widget opened for itself (classroom-off path).
   *
   * The widget keeps its Realtime session alive when Angular detaches the
   * element, so without this the browser goes on showing the microphone as
   * in use on /home. The session stays connected: only the capture stops.
   */
  private releaseWidgetOwnedMic(reason: string): void {
    try {
      (window as any).__aiChatReleaseMic?.(reason);
    } catch (err) {
      console.warn('[StepAI] Widget mic release failed', err);
    }
  }

  /** Re-arm the widget's own capture when the student returns to a course. */
  private resumeWidgetOwnedMic(): void {
    try {
      (window as any).__aiChatResumeMic?.();
    } catch (err) {
      console.warn('[StepAI] Widget mic resume failed', err);
    }
  }

  /**
   * Drop the classroom camera/mic while keeping the LiveKit room connected,
   * so presence survives a trip to /home but the capture indicators clear.
   */
  private releaseClassroomMedia(): void {
    if (!this._feature_classroom) return;
    void this._virtualClassroomService
      .unpublishTracks()
      .catch((err) => console.warn('[CompactLayout] Classroom unpublish failed', err));
  }

  /** Republish the classroom tracks on returning to a course. */
  private resumeClassroomMedia(): void {
    if (!this._feature_classroom) return;
    void this._virtualClassroomService
      .republishTracks()
      .catch((err) => console.warn('[CompactLayout] Classroom republish failed', err));
  }

  /**
   * Update widget attributes from config
   * Sets single ai-content attribute with JSON string
   * Widget stays connected - just switches agents via attribute updates
   */
  private updateWidgetAttributes(config: GlobalAiChatConfig): void {
    if (!this.aiChatTeacherElement?.nativeElement) return;

    // Fire-and-forget: ensure inject before attribute write when classroom is on.
    // If already injected / classroom off, apply immediately.
    if (this._feature_classroom && !this.aiMicInjected) {
      void this.ensureSharedAiMic().then(() => this.applyWidgetAttributes(config));
      return;
    }

    this.applyWidgetAttributes(config);
  }

  private applyWidgetAttributes(config: GlobalAiChatConfig): void {
    if (!this.aiChatTeacherElement?.nativeElement) return;

    // Both other call sites are one-shot and give up when the view ref has
    // not resolved yet, which left the host deaf to every widget event for
    // the whole session: ai-chat-flow-terminal never arrived, so the
    // course-entry welcome could only be released by its 106s timeout while
    // the student sat in silence. Here the element demonstrably exists.
    this.setupWidgetEventListeners();

    const element = this.aiChatTeacherElement.nativeElement as any;
    const aiContent = this.buildAiContent(config);

    // Remember muted preloads so drawer-open can re-speak once (not every open).
    if (
      aiContent.muteAi &&
      config.muteAi === false &&
      config.agentId &&
      config.agentId !== 'agent.listener'
    ) {
      this.pendingMutedSpeak = true;
    }

    if (writeAiContent(element, aiContent)) {
      console.log('[StepAI] widget ai-content applied', {
        ...summarizeAiContent(aiContent),
        pendingMutedSpeak: this.pendingMutedSpeak,
        connected: this.isWidgetConnected,
      });

      // Debug: ask widget to dump full chat window after config is applied
      if ((this.ai_chat_debug || config.debug) && typeof element.dumpChatWindow === 'function') {
        // Defer so React transcript state / attribute apply can settle
        setTimeout(() => element.dumpChatWindow('angular.step-config'), 0);
      }
    }

    // Apply mute state immediately with retry mechanism (for reliable mute button)
    this.applyMuteStateImmediately(this.effectiveMuteAi(config.muteAi), element);
  }

  /** The one serialization of a step config for the widget's `ai-content`. */
  private buildAiContent(config: GlobalAiChatConfig): Record<string, any> {
    const accessToken = this._authRepo.getAccessToken() || '';
    const authUser = this._authRepo.getUser?.() as any;
    const studentId =
      this._role === UserRole.Student
        ? (authUser?.person_id ?? authUser?.user_id)
        : undefined;
    // /academy/{id} is the enrollment id (t_ot_student_courseid).
    const enrollmentMatch = this._router?.url?.match?.(/\/academy\/(\d+)/);
    const studentCourseId = enrollmentMatch ? enrollmentMatch[1] : undefined;
    const studentCourseStepId = this.stepAudio.currentStepId ? String(this.stepAudio.currentStepId) : undefined;
    // The course, not the enrollment: course-scoped AI knowledge is keyed by it.
    const courseId = this._globalAiChatService.getAcademyCourseId();
    const stepIndex =
      typeof (config as any).stepIndex === 'number'
        ? (config as any).stepIndex
        : undefined;

    const aiContent: any = {
      agentId: config.agentId,
      lang: config.lang,
      contentLang: config.contentLang,
      role: config.role,
      debug: this.ai_chat_debug,  // From environment
      apiBaseUrl: config.apiBaseUrl,
      // api4 root for /v1/* calls (knowledge, learner state, supervisor notify).
      // A different host from apiBaseUrl, which is the AI chat backend.
      lmsApiBaseUrl: this.appConfig.get('serverUrl'),
      fullMode: this.ai_chat_full_mode,  // From environment
      autoStart: this.canAutoStartAiChat(),
      muteAi: this.effectiveMuteAi(config.muteAi),
      micDeferred: !this.isAcademyContext || !this._globalAiChatService.isCourseAiOn(),
      flowSeq: config.flowSeq,
      stepVisitId: config.stepVisitId ?? config.flowSeq,
      completionAttemptId: config.completionAttemptId,
      hostManagedRecording: true,
      // The tutor only answers "where is the X button" for buttons this student can see.
      enabledFeatures: [
        'feature_ihaveawin',
        'feature_clearword',
        'feature_chat_supervisor',
        'feature_game',
        'feature_ai',
        'feature_orientation_tour',
      ].filter((key) => this._featureSettings.isFeatureEnabled(key)),
    };

    // JWT for hardened /session (Bearer). Also mirror on window for early fetch.
    if (accessToken) {
      aiContent.authToken = accessToken;
      try {
        (window as any).__aiChatAuthToken = accessToken;
      } catch {
        /* ignore */
      }
    }
    if (studentId != null && studentId !== '') {
      aiContent.studentId = String(studentId);
    }
    if (courseId) {
      aiContent.courseId = String(courseId);
    }
    if (studentCourseId) {
      aiContent.studentCourseId = String(studentCourseId);
    }
    if (studentCourseStepId) {
      aiContent.hostManagedRecording = true;
      aiContent.studentCourseStepId = String(studentCourseStepId);
    }
    if (typeof stepIndex === 'number' && Number.isFinite(stepIndex)) {
      aiContent.stepIndex = stepIndex;
    }
    // Course opted into keeping the student's spoken answers. Only sent when
    // there is a step to file the recording under.
    if (studentCourseStepId && this._globalAiChatService.isAudioRecordingEnabled()) {
      aiContent.recordAudio = true;
    }
    const semanticTurns = this._globalAiChatService.getSemanticTurnsMode();
    if (semanticTurns === 'shadow') {
      aiContent.semanticShadow = true;
    } else if (semanticTurns === 'live') {
      aiContent.semanticTurns = true;
    }
    
    // Only include content if it exists
    if (config.content) {
      aiContent.content = config.content;
    }
    return aiContent;
  }

  /**
   * Immediately mute the widget (bypasses attribute update delay)
   * Called when switching to listener agent
   */
  private muteWidgetImmediately(): void {
    if (!this.aiChatTeacherElement?.nativeElement) return;
    
    const element = this.aiChatTeacherElement.nativeElement as any;
    
    // Call mute method directly if available
    if (typeof element.muteAi === 'function') {
      try {
        element.muteAi(true);
        console.log('🔇 Immediately muted widget (listener mode)');
      } catch (error) {
        console.error('❌ Failed to mute widget immediately:', error);
      }
    } else {
      console.warn('⚠️ Widget muteAi method not available');
    }
  }

  /**
   * Apply mute state immediately with retry mechanism
   * Ensures mute button works reliably even if widget is not immediately ready
   */
  private applyMuteStateImmediately(shouldMute: boolean, element: any, retryCount: number = 0): void {
    const maxRetries = 3;
    
    try {
      if (shouldMute) {
        if (typeof element.muteAi === 'function') {
          element.muteAi(true);
          console.log('🔇 Widget muted immediately');
        } else if (retryCount < maxRetries) {
          // Retry after a short delay (widget might not be ready)
          setTimeout(() => {
            this.applyMuteStateImmediately(shouldMute, element, retryCount + 1);
          }, 100);
        } else {
          console.warn('⚠️ Widget muteAi method not available after retries');
        }
      } else {
        if (typeof element.unmuteAi === 'function') {
          element.unmuteAi();
          console.log('🔊 Widget unmuted immediately');
        } else if (retryCount < maxRetries) {
          setTimeout(() => {
            this.applyMuteStateImmediately(shouldMute, element, retryCount + 1);
          }, 100);
        } else {
          console.warn('⚠️ Widget unmuteAi method not available after retries');
        }
      }
    } catch (error) {
      console.error('❌ Error applying mute state:', error);
      if (retryCount < maxRetries) {
        setTimeout(() => {
          this.applyMuteStateImmediately(shouldMute, element, retryCount + 1);
        }, 100);
      }
    }
  }

  /**
   * Mute button handler
   * Toggles mute state and immediately applies it to the widget
   */
  toggleAiMute(): void {
    // Only allow muting if feature_ai is enabled
    if (!this.feature_ai) {
      return;
    }
    
    // Get current state before toggling
    const currentMuted = this._globalAiChatService.isMuted();
    const newMutedState = !currentMuted;
    
    // Toggle in service (updates config)
    this._globalAiChatService.toggleMute();
    
    // Immediately apply mute state to widget (for instant feedback)
    if (this.aiChatTeacherElement?.nativeElement) {
      const element = this.aiChatTeacherElement.nativeElement as any;
      this.applyMuteStateImmediately(newMutedState, element);
    }
  }

  /**
   * Student turning their AI tutor off or back on.
   *
   * Turning it off pages the supervisor, so it asks first. Turning it back on
   * is the student undoing their own choice and needs no confirmation.
   */
  toggleAiOptOut(): void {
    if (!this.aiFeatureAvailable || this.aiOptOutSaving) {
      return;
    }

    if (this.aiOptedOut) {
      this.saveAiOptOut(false);
      return;
    }

    this._fuseConfirmationService
      .open({
        title: this._translateService.instant('ai.optOut.confirmTitle'),
        message: this._translateService.instant('ai.optOut.confirmMessage'),
        icon: { show: true, name: 'heroicons_outline:no-symbol', color: 'warning' },
        actions: {
          confirm: { show: true, label: this._translateService.instant('ai.optOut.confirmButton'), color: 'warn' },
          cancel: { show: true, label: this._translateService.instant('ai.optOut.cancelButton') },
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result === 'confirmed') {
          this.saveAiOptOut(true);
        }
      });
  }

  private saveAiOptOut(optedOut: boolean): void {
    this.aiOptOutSaving = true;

    // /academy/{student_course_id}/... — lets the supervisor's notification
    // link straight to the checksheet they now have to validate by hand.
    const courseMatch = (this._router.url || '').match(/\/academy\/(\d+)/);
    const studentCourseId = courseMatch ? parseInt(courseMatch[1], 10) : null;

    this._globalAiChatService
      .setAiOptedOut(optedOut, studentCourseId)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((saved) => {
        this.aiOptOutSaving = false;
        if (!saved) {
          return;
        }
        this.applyAiOptedOut(optedOut);
      });
  }

  /**
   * Mirror the saved choice into the layout: tear the widget down, or bring it
   * back the same way entering /academy/* does.
   */
  private applyAiOptedOut(optedOut: boolean): void {
    this.aiOptedOut = optedOut;
    // feature_ai and the supervisor chat history are driven by the aiActive$
    // subscription, which the service has already pushed by the time this runs.

    if (optedOut) {
      this.muteWidgetImmediately();
      this.releaseSharedAiMic();
      this.releaseWidgetOwnedMic('ai-opted-out');
      this.widgetListenersBound = false;
      this.isWidgetConnected = false;
      return;
    }

    if (!this.isAcademyContext) {
      return;
    }

    // The widget element is re-created by *ngIf on this tick, so bind and
    // configure it only after Angular has put it back in the DOM.
    this._globalAiChatService.updateConfig({ showWidget: true });
    setTimeout(() => {
      void this.ensureSharedAiMic().then(() => {
        this.setupWidgetEventListeners();
        this.updateWidgetAttributes(this._globalAiChatService.getCurrentConfig());
      });
    }, 100);
  }

  /**
   * Check if AI is muted (for UI state)
   */
  get isAiMuted(): boolean {
    // Return false if feature_ai is disabled (no mute state)
    if (!this.feature_ai) {
      return false;
    }
    return this._globalAiChatService.isMuted();
  }

  /**
   * Check if mute button should be disabled (in listener mode)
   */
  get isMuteButtonDisabled(): boolean {
    return this.aiChatConfig.agentId === 'agent.listener';
  }

  /**
   * On destroy
   */
  /** Open the help / FAQ dialog */
  openHelpDialog(): void {
    import('app/layout/common/help-dialog/help-dialog.component').then((m) => {
      this._dialog.open(m.HelpDialogComponent, {
        width: '900px',
        maxWidth: '95vw',
        maxHeight: '85vh',
        panelClass: 'help-dialog-panel',
        data: { lang: this._translateService.currentLang || 'en' },
      });
    });
  }

  ngOnDestroy(): void {
    // Detach video track from inline element
    this.detachVideoFromInlineElement();
    this.releaseSharedAiMic();
    this.releaseWidgetOwnedMic('layout-destroy');
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
    
    // Note: We don't disconnect the widget here - it stays connected
    // The widget will handle cleanup when the page is closed
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Update sidebar visibility based on user role
   */
  private updateSidebarVisibility(): void {
    this._adminNavigationService.updateSidebarVisibility();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  gotoLiveSystem() {
    console.log('route to backend ---- sso');

    // set the form params
    const access_token = this._authRepo.getAccessToken();
    const userid = this._authRepo.getUser().user_id;
    const fid = this._authRepo.getUser().person_id;
    const role = this._authRepo.getUser().role;

    const myform = document.createElement('form');
    myform.method = 'POST';
    myform.action = this.appConfig.get('otrainingsystem') + '/belogin.php';
    myform.style.display = 'none';
    //myform.append('Content-Type', 'application/x-www-form-urlencoded');
    //myform.append('Accept', 'text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,image/apng,*/*;q=0.8');

    const inputAuth = document.createElement('input');
    inputAuth.type = 'hidden';
    inputAuth.name = 'Authorization';
    inputAuth.value = access_token;
    myform.appendChild(inputAuth);

    const inputUid = document.createElement('input');
    inputUid.type = 'hidden';
    inputUid.name = 'uid';
    inputUid.value = '' + userid;
    myform.appendChild(inputUid);

    const inputFid = document.createElement('input');
    inputFid.type = 'hidden';
    inputFid.name = 'fid';
    inputFid.value = '' + fid;
    myform.appendChild(inputFid);

    const inputRole = document.createElement('input');
    inputRole.type = 'hidden';
    inputRole.name = 'role';
    inputRole.value = '' + role;
    myform.appendChild(inputRole);

    document.body.appendChild(myform);
    myform.target = '_blank';
    myform.submit();
  }

  callSupervisor() {
    console.log('call supervisor');
    
    // If drawer is open and already in chat mode, close it
    if (this.chatDrawer.opened && this.drawerMode === 'chat') {
      this.chatDrawer.close();
      // Chat drawer closed - notify service
      this._selfieDialogService.setChatOpen(false);
      this.markAiChatDrawerClosed('toggle-close');
      // Detach video track from inline element
      this.detachVideoFromInlineElement();
    } else {
      // Otherwise, set mode to chat and open/switch
      this.drawerMode = 'chat';
      this.chatDrawer.open();
      
      // Chat drawer opened - notify service
      this._selfieDialogService.setChatOpen(true);
      
      // Load messages from API when drawer opens (for students)
      if (this._role === UserRole.Student) {
        console.log('[Chat] Drawer opened — companyAckEnabled:', this.companyAckEnabled, '| companySettings:', this._featureSettings.getCompanySettings()?.messaging);
        this.loadChatMessages();
        // Attach video track to inline element for students
        setTimeout(() => {
          this.attachVideoToInlineElement();
        }, 200);
      }
      
      // Unmute + re-speak current step once the panel is visible
      if (this.feature_ai) {
        setTimeout(() => this.releaseAiChatDrawerAndSpeak('manual-open'), 100);
      }
    }
  }

  // Handle Enter to send and Shift+Enter for newline
  onChatKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendChatMessage();
    }
  }

  // Send chat message helper
  sendChatMessage(): void {
    this.chatSendError = '';
    const message = (this._role === UserRole.Student ? this.chatInput : this.adminChatInput).trim();
    const uploadedAttachments = this.pendingChatAttachments.filter(a => a.uploaded && a.result);
    const stillUploading = this.pendingChatAttachments.some(a => a.uploading);

    if (!message && uploadedAttachments.length === 0) { return; }
    if (stillUploading) { return; } // Wait for uploads to finish

    const now = new Date();
    const currentUser = this._authRepo.getUser();
    const myRole = this._role === UserRole.Student ? 'student' : 'supervisor';

    // Push optimistic text message
    if (message) {
      this.chatMessages.push({
        sender: 'me',
        text: message,
        timestamp: Date.now(),
        senderName: 'You',
        senderRole: myRole,
        senderAvatar: this._resolveAvatarUrl(currentUser?.profile_image),
        date: now.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        time: now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
        status: 'sent',
        attachedFiles: uploadedAttachments.map(a => a.result!),
      });
    } else if (uploadedAttachments.length > 0) {
      // File-only message
      this.chatMessages.push({
        sender: 'me',
        text: '',
        timestamp: Date.now(),
        senderName: 'You',
        senderRole: myRole,
        senderAvatar: this._resolveAvatarUrl(currentUser?.profile_image),
        date: now.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        time: now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
        status: 'sent',
        attachedFiles: uploadedAttachments.map(a => a.result!),
      });
    }

    // Re-group so the optimistic message appears immediately
    this.groupMessagesByStep();

    // Send via API
    const attachedFileIds = uploadedAttachments.map(a => a.result!.id);
    const payload: Partial<ApiChatMessage> & { attached_file_ids?: number[] } = {
      message: message || '📎',
      sender_type: myRole as 'student' | 'supervisor',
      sender_id: currentUser?.person_id || 0,
      student_id: this._role === UserRole.Student
        ? (currentUser?.person_id || 0)
        : (this.selectedStudent?.t_ot_studentid || 0),
      supervisor_id: this._role !== UserRole.Student
        ? (currentUser?.person_id || 0)
        : 0,
      subject: '',
      student_course_step_id: this.replyToStep?.stepId || undefined,
      student_course_id: this.replyToStep?.studentCourseId || undefined,
      attached_file_ids: attachedFileIds,
    };

    const outgoingMessage = this.chatMessages[this.chatMessages.length - 1];
    this._chatService.sendMessage(payload as Partial<ApiChatMessage>)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (response) => {
          uploadedAttachments.forEach(a => this._chatFileService.revokePreview(a.previewUrl));
          outgoingMessage.status = 'delivered';
          outgoingMessage.messageId = Number(response.data.id);
          // The server resolves the student requests this reply answers — mirror
          // that locally so the pill does not stay orange until the next reload.
          if (myRole === 'supervisor') {
            this.chatMessages.forEach((m) => {
              if (m.senderRole === 'student' && Number(m.stepId || 0) === Number(payload.student_course_step_id || 0) &&
                Number(m.messageId) < outgoingMessage.messageId && (m.attentionState === 'new_message' || m.attentionState === 'needs_supervisor_action')) {
                m.attentionState = 'resolved';
              }
            });
          }
        },
        error: () => {
          const visibleStudentId = this._role === UserRole.Student ? this._authRepo.getUser()?.person_id : this.selectedStudent?.t_ot_studentid;
          if (Number(visibleStudentId) !== Number(payload.student_id)) return;
          this.chatMessages = this.chatMessages.filter(item => item !== outgoingMessage);
          this.groupMessagesByStep();
          this.chatSendError = this._translateService.instant('chat.direct.sendError');
          if (this._role === UserRole.Student) this.chatInput = message;
          else this.adminChatInput = message;
          this.pendingChatAttachments = uploadedAttachments;
          this._changeDetectorRef.markForCheck();
        }
      });

    // Clear pending attachments (revoke preview URLs)
    // Retain the uploaded file references until send succeeds so retry is possible.
    this.pendingChatAttachments = [];
    this.pastedImageUrl = null;

    // Persist & clear input
    this.saveMessagesToStorage();
    if (this._role === UserRole.Student) { this.chatInput = ''; } else { this.adminChatInput = ''; }
    if (this._role === UserRole.Student) { this.scrollToBottom(); } else { this.scrollToBottomAdmin(); }
  }

  // Handle paste events for image attachments — now uses real uploads
  onChatPaste(event: ClipboardEvent): void {
    if (!event.clipboardData) { return; }
    const files = event.clipboardData.files;
    let handled = false;
    if (files && files.length) {
      for (let i = 0; i < files.length; i++) {
        if (files[i].type.startsWith('image/') || files[i].size > 0) {
          this._addPendingAttachment(files[i]);
          handled = true;
        }
      }
    }
    if (!handled) {
      const items = event.clipboardData.items;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          const file = items[i].getAsFile();
          if (file) { this._addPendingAttachment(file); handled = true; }
        }
      }
    }
    if (handled) { event.preventDefault(); }
  }

  // Attachment via file input — now uses real uploads
  onAttachmentSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      for (let i = 0; i < input.files.length; i++) {
        this._addPendingAttachment(input.files[i]);
      }
    }
    input.value = '';
  }

  /** Add a file to pendingChatAttachments and immediately start uploading. */
  private _addPendingAttachment(file: File): void {
    const pending: PendingChatAttachment = {
      file,
      previewUrl: this._chatFileService.buildPreview(file),
      uploading: true,
      uploaded: false,
      error: false,
      result: null,
    };
    this.pendingChatAttachments = [...this.pendingChatAttachments, pending];
    const idx = this.pendingChatAttachments.length - 1;

    const currentUser = this._authRepo.getUser();
    const studentId = this._role === UserRole.Student
      ? (currentUser?.person_id || 0)
      : (this.selectedStudent?.t_ot_studentid || 0);
    const companyId = this._role === UserRole.Student
      ? (currentUser?.company_id || 0)
      : (this.selectedStudent?.company_id || 0);
    const token = this._authRepo.getAccessToken() || '';

    if (!companyId || !studentId) { return; }

    this._chatFileService.upload(file, companyId, studentId, token).subscribe({
      next: (res) => {
        this.pendingChatAttachments = this.pendingChatAttachments.map((a, i) =>
          i === idx ? { ...a, uploading: false, uploaded: true, result: res.data } : a
        );
      },
      error: () => {
        this.pendingChatAttachments = this.pendingChatAttachments.map((a, i) =>
          i === idx ? { ...a, uploading: false, error: true } : a
        );
      },
    });
  }

  /** Remove a pending attachment (delete from server if already uploaded). */
  removePendingAttachment(idx: number): void {
    const att = this.pendingChatAttachments[idx];
    if (!att) return;
    if (att.result?.id) {
      const token = this._authRepo.getAccessToken() || '';
      this._chatFileService.delete(att.result.id, token).subscribe();
    }
    this._chatFileService.revokePreview(att.previewUrl);
    this.pendingChatAttachments = this.pendingChatAttachments.filter((_, i) => i !== idx);
  }

  /** Format a file size number for display. */
  formatFileSize(bytes: number): string {
    return this._chatFileService.formatSize(bytes);
  }

  // Track which image attachment IDs have failed to load (show file fallback instead)
  private _failedImages = new Set<number>();

  onImageError(fileId: number): void {
    this._failedImages.add(fileId);
  }

  isImageFailed(fileId: number): boolean {
    return this._failedImages.has(fileId);
  }

  // ── Lightbox ──────────────────────────────────────────────────────────────

  openLightbox(images: { url: string; name: string }[], startIndex: number): void {
    this.lightboxImages = images;
    this.lightboxIndex = startIndex;
    this.lightboxOpen = true;
  }

  closeLightbox(): void {
    this.lightboxOpen = false;
  }

  lightboxPrev(): void {
    if (this.lightboxIndex > 0) { this.lightboxIndex--; }
    else { this.lightboxIndex = this.lightboxImages.length - 1; }
  }

  lightboxNext(): void {
    if (this.lightboxIndex < this.lightboxImages.length - 1) { this.lightboxIndex++; }
    else { this.lightboxIndex = 0; }
  }

  /** Build a token-appended serve URL for a chat file id. */
  chatFileServeUrl(fileId: number): string {
    const token = this._authRepo.getAccessToken() || '';
    return this._chatFileService.serveUrl(fileId, token);
  }

  /**
   * Build a serve URL for any attachment, handling both chat-file and step-file sources.
   * Uses f.url (set by the API) which already contains the correct endpoint path.
   */
  attachmentServeUrl(f: ChatFileUploadResult): string {
    const token = this._authRepo.getAccessToken() || '';
    if (f.url) {
      // f.url is relative, e.g. /api/v1/step-file/5 or /api/v1/chat-file/3
      // Strip trailing /api from serverUrl to get the bare origin
      const origin = (this.appConfig.get('serverUrl') || '').replace(/\/api$/, '');
      return `${origin}${f.url}?token=${encodeURIComponent(token)}`;
    }
    return this._chatFileService.serveUrl(f.id, token);
  }

  /** Collect all image attachments from a list of messages for lightbox gallery. */
  collectImageAttachments(messages: ChatMessage[]): { url: string; name: string }[] {
    const result: { url: string; name: string }[] = [];
    for (const msg of messages) {
      for (const f of (msg.attachedFiles || [])) {
        if (f.is_image) {
          result.push({ url: this.attachmentServeUrl(f), name: f.original_name });
        }
      }
    }
    return result;
  }

  // --- Persistence helpers (simple local storage) ---
  private getStorageKey(): string {
    const userId = this._authRepo.getUser()?.user_id ?? 'anon';
    return `chatMessages_${userId}`;
  }

  private loadMessagesFromStorage(): void {
    try {
      const raw = localStorage.getItem(this.getStorageKey());
      if (raw) {
        const parsed = JSON.parse(raw) as ChatMessage[];
        if (Array.isArray(parsed)) {
          this.chatMessages = parsed;
        }
      }
    } catch (e) {
      console.warn('Failed to load chat messages from storage', e);
    }
  }

  private saveMessagesToStorage(): void {
    try {
      localStorage.setItem(this.getStorageKey(), JSON.stringify(this.chatMessages));
    } catch (e) {
      console.warn('Failed to save chat messages to storage', e);
    }
  }

  

  goToGameHub(): void {
    this._router.navigate(['/game-hub']);
  }

  clearWords() {
    console.log('clear words');
    
    // If drawer is open and already in dictionary mode, close it
    if (this.chatDrawer.opened && this.drawerMode === 'dictionary') {
      this.chatDrawer.close();
      this._selfieDialogService.setChatOpen(false);
      this.markAiChatDrawerClosed('dictionary-toggle-close');
    } else {
      // Leaving chat mode: dictionary has no video, so restore floating selfie
      if (this.drawerMode === 'chat') {
        this._selfieDialogService.setChatOpen(false);
        this.detachVideoFromInlineElement();
      }
      // Otherwise, set mode to dictionary and open/switch
      this.drawerMode = 'dictionary';
      this.chatDrawer.open();
      // Drawer still open — keep AI unmuted if already ready
      if (this.feature_ai && !this.aiChatDrawerReady) {
        this.releaseAiChatDrawerAndSpeak('dictionary-open');
      }
    }

    //window.open('https://studio.studema.at/glossary/glossar/', '_blank');
  }

  openWinDialog(): void {
    const user = this._authRepo.getUser();
    if (!user?.person_id) return;
    const url = this._router.url || '';
    const courseMatch = url.match(/\/academy\/(\d+)/);
    const studentCourseId = courseMatch ? parseInt(courseMatch[1], 10) : undefined;
    const stepMatch = url.match(/step_id[=:](\d+)/i);
    const studentCourseStepId = stepMatch ? parseInt(stepMatch[1], 10) : undefined;

    const rawWinHeader = this._featureSettings.getCompanySettings()?.branding?.win_header;
    const winHeaderImage = rawWinHeader ? this._resolveMediaUrl(rawWinHeader) : undefined;

    const data: IHaveAWinDialogData = {
      studentId: user.person_id,
      studentCourseId,
      studentCourseStepId,
      winHeaderImage,
    };

    this._dialog.open(IHaveAWinDialogComponent, {
      width: '520px',
      maxWidth: '95vw',
      panelClass: ['win-dialog'],
      data,
    });
  }

  /**
   * Converts a stored branding value (numeric media_id or relative path) to a displayable URL.
   * this.appConfig.get('serverUrl') already contains the /api prefix (e.g. https://api.example.com/api),
   * so public media is served at {serverUrl}/public/company-media/{id}.
   */
  private _resolveMediaUrl(value: string | number | null | undefined): string {
    if (!value) return '';
    const val = String(value).trim();
    if (!val) return '';
    if (/^\d+$/.test(val)) {
      return `${this.appConfig.get('serverUrl')}/public/company-media/${val}`;
    }
    return val.startsWith('http') ? val : `${this.appConfig.get('serverUrl')}/${val}`;
  }

  closeDrawer(): void {
    this.replyToStep = null;
    this.chatDrawer.close();
    // Chat drawer closed - notify service
    this._selfieDialogService.setChatOpen(false);
    this.markAiChatDrawerClosed('closeDrawer');
    // Detach video track from inline element
    this.detachVideoFromInlineElement();
    // Reset student chat state when closing drawer
    this.selectedStudent = null;
    this.isInStudentChat = false;
    if (this.directChat) this.directChat.selected = null;
  }

  /**
   * Attach video track to inline video element in chat sidebar
   */
  attachVideoToInlineElement(): void {
    // Wait a bit for the element to be rendered in the DOM
    setTimeout(() => {
      if (!this.inlineVideoElement?.nativeElement) {
        console.warn('⚠️ [CompactLayout] Inline video element not available, retrying...');
        // Retry after a short delay
        setTimeout(() => this.attachVideoToInlineElement(), 300);
        return;
      }
      this.doAttachVideoTrack();
    }, 100);
  }

  private doAttachVideoTrack(): void {
    if (!this.inlineVideoElement?.nativeElement) {
      console.warn('⚠️ [CompactLayout] Inline video element not available');
      return;
    }

    const room = this._virtualClassroomService.getRoom();
    if (!room) {
      console.warn('⚠️ [CompactLayout] No room available for inline video');
      return;
    }

    const localParticipant = room.localParticipant;
    if (!localParticipant) {
      console.warn('⚠️ [CompactLayout] No local participant');
      return;
    }

    // Get video track publication
    const videoPubs = Array.from(localParticipant.videoTrackPublications.values());
    const videoPub = videoPubs[0];
    const videoTrack = videoPub?.track;

    if (videoTrack) {
      try {
        this.inlineVideoTrack = videoTrack;
        videoTrack.attach(this.inlineVideoElement.nativeElement);
        this.hasVideoTrack = true;
        
        // Also get audio track for mute/unmute functionality
        const audioPubs = Array.from(localParticipant.audioTrackPublications.values());
        const audioPub = audioPubs[0];
        this.inlineAudioTrack = audioPub?.track;
        if (this.inlineAudioTrack) {
          console.log('✅ [CompactLayout] Audio track found for inline video');
        }
        
        console.log('✅ [CompactLayout] Video track attached to inline element');
      } catch (err) {
        console.warn('⚠️ [CompactLayout] Failed to attach video track:', err);
      }
    } else {
      // Listen for when track becomes available
      const trackPublishedListener = (pub: any) => {
        if (pub.kind === 'video' && pub.track) {
          try {
            this.inlineVideoTrack = pub.track;
            pub.track.attach(this.inlineVideoElement.nativeElement);
            this.hasVideoTrack = true;
            
            // Also get audio track for mute/unmute functionality
            const audioPubs = Array.from(localParticipant.audioTrackPublications.values());
            const audioPub = audioPubs[0];
            this.inlineAudioTrack = audioPub?.track;
            if (this.inlineAudioTrack) {
              console.log('✅ [CompactLayout] Audio track found for inline video (from event)');
            }
            
            console.log('✅ [CompactLayout] Video track attached to inline element (from event)');
            room.off(RoomEvent.LocalTrackPublished, trackPublishedListener);
          } catch (err) {
            console.warn('⚠️ [CompactLayout] Failed to attach video track:', err);
          }
        }
      };
      room.on(RoomEvent.LocalTrackPublished, trackPublishedListener);
      console.log('👂 [CompactLayout] Listening for LocalTrackPublished events for inline video');
    }
  }

  /**
   * Detach video track from inline video element
   */
  detachVideoFromInlineElement(): void {
    if (this.inlineVideoTrack && this.inlineVideoElement?.nativeElement) {
      try {
        // Detach only the inline element so other attachments (e.g. selfie dialog) stay intact
        this.inlineVideoTrack.detach(this.inlineVideoElement.nativeElement);
        this.inlineVideoTrack = null;
        this.hasVideoTrack = false;
        this.inlineAudioTrack = null;
        this.isInlineAudioMuted = true;
        this.isInlineVideoHidden = false;
        console.log('🧹 [CompactLayout] Detached video track from inline element');
      } catch (err) {
        console.warn('⚠️ [CompactLayout] Error detaching video track:', err);
      }
    }
  }

  /**
   * Toggle audio mute/unmute for inline video
   */
  toggleInlineAudio(): void {
    if (this.inlineAudioTrack) {
      if (this.isInlineAudioMuted) {
        this.inlineAudioTrack.enabled = true;
      } else {
        this.inlineAudioTrack.enabled = false;
      }
      this.isInlineAudioMuted = !this.isInlineAudioMuted;
      console.log('🎤 [CompactLayout] Inline audio ' + (this.isInlineAudioMuted ? 'muted' : 'unmuted'));
    }
  }

  /**
   * Toggle video on/off for inline video
   */
  async toggleInlineVideo(): Promise<void> {
    const room = this._virtualClassroomService.getRoom();
    if (!room) {
      console.warn('⚠️ [CompactLayout] Cannot toggle inline video - no room available');
      return;
    }

    const localParticipant = room.localParticipant;
    const videoPubs = Array.from(localParticipant.videoTrackPublications.values());
    const videoPub = videoPubs[0];

    if (this.isInlineVideoHidden) {
      // Turn video ON - publish video track
      if (this.inlineVideoTrack && !videoPub) {
        await localParticipant.publishTrack(this.inlineVideoTrack);
        if (this.inlineVideoElement?.nativeElement) {
          this.inlineVideoTrack.attach(this.inlineVideoElement.nativeElement);
        }
        console.log('✅ [CompactLayout] Inline video track published');
        this.isInlineVideoHidden = false;
      } else if (videoPub && videoPub.track) {
        if (this.inlineVideoElement?.nativeElement) {
          videoPub.track.attach(this.inlineVideoElement.nativeElement);
          this.inlineVideoTrack = videoPub.track;
        }
        console.log('✅ [CompactLayout] Inline video track re-attached');
        this.isInlineVideoHidden = false;
      }
    } else {
      // Turn video OFF - unpublish video track
      if (videoPub && videoPub.track) {
        await localParticipant.unpublishTrack(videoPub.track);
        if (this.inlineVideoElement?.nativeElement) {
          videoPub.track.detach();
        }
        console.log('✅ [CompactLayout] Inline video track unpublished');
        this.isInlineVideoHidden = true;
      }
    }
  }

  /**
   * Open chat with a specific student (for admins/supervisors)
   */
  /**
   * When the phuture-comments widget fires this event (supervisor view is active on a step),
   * mark all messages for that step as read.
   */
  @HostListener('document:phuture-comments-supervisor-viewed', ['$event'])
  onPhutureCommentsSupervisorViewed(event: CustomEvent): void {
    const stepId = event?.detail?.step_id;
    if (stepId && this._role !== UserRole.Student) {
      this._chatService.markAsReadByStep(stepId)
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe({
          next: () => {
            this._lifecycleLog('onPhutureCommentsSupervisorViewed: marked read', { stepId }, '#16a34a');
            this.loadTotalUnreadCount();
          },
          error: (err) => this._lifecycleLog('onPhutureCommentsSupervisorViewed: error', { err }, '#dc2626')
        });
    }
  }

  /**
   * When a student clicks OK in phuture-comments, mark that step's messages as read.
   */
  @HostListener('document:phuture-comments-student-ack', ['$event'])
  onPhutureCommentsStudentAck(event: CustomEvent): void {
    const stepId = event?.detail?.step_id;
    if (stepId && this._role === UserRole.Student) {
      this._chatService.markAsReadByStep(stepId, true)
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe({
          next: () => {
            this._lifecycleLog('onPhutureCommentsStudentAck: marked read', { stepId }, '#16a34a');
            this.loadStudentUnreadCount();
            const nowIso = new Date().toISOString();
            this.chatMessages = this.chatMessages.map(m =>
              m.stepId === stepId && m.sender !== 'me' ? { ...m, readAt: m.readAt || nowIso, status: 'read' as const } : m
            );
          },
          error: (err) => this._lifecycleLog('onPhutureCommentsStudentAck: error', { err }, '#dc2626')
        });
    }
  }

  /**
   * When a student opens phuture-comments, the widget fires this event.
   * Mark the step's messages as read automatically.
   */
  @HostListener('document:phuture-comments-student-viewed', ['$event'])
  onPhutureCommentsStudentViewed(event: CustomEvent): void {
    const stepId = event?.detail?.step_id;
    if (stepId && this._role === UserRole.Student) {
      this._chatService.markAsReadByStep(stepId)
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe({
          next: () => {
            this._lifecycleLog('onPhutureCommentsStudentViewed: marked read', { stepId }, '#16a34a');
            this.loadStudentUnreadCount();
            const nowIso = new Date().toISOString();
            this.chatMessages = this.chatMessages.map(m =>
              m.stepId === stepId && m.sender !== 'me' && (!m.ackRequired || m.ackAt)
                ? { ...m, readAt: m.readAt || nowIso, status: 'read' as const } : m
            );
          },
          error: (err) => this._lifecycleLog('onPhutureCommentsStudentViewed: error', { err }, '#dc2626')
        });
    }
  }

  openStudentChat(student: Student): void {
    this.directChat?.selectCategory('students');
    this.replyToStep = null;
    this.chatSendError = '';
    this.selectedStudent = student;
    this.isInStudentChat = true;
    this._lifecycleLog('openStudentChat()', {
      studentId: student?.t_ot_studentid,
      studentName: student?.name,
    }, '#f59e0b');
    
    // Supervisors and admins never need to ACK — auto-mark all read when opening chat
    if (this._role !== UserRole.Student && student.t_ot_studentid) {
      this.clearUnreadCount(student.t_ot_studentid);
    }

    if (student.t_ot_studentid) {
      this.loadStudentChatMessages(student.t_ot_studentid);
    }
    
    console.log('Opening chat with student:', student.name);
  }

  /**
   * Load chat messages for a specific student from the API (supervisor view)
   */
  private loadStudentChatMessages(studentId: number): void {
    this.chatMessages = [];
    this.chatPage = 1;
    this.twinInfo = null;
    
    this._chatService.getMessagesPage(studentId, 1, this.chatPerPage)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: ({ messages: apiMessages, total }) => {
          if (!apiMessages || apiMessages.length === 0) {
            this.chatMessages = [];
            this.chatTotalMessages = total;
            this.chatHasMore = false;
            return;
          }
          
          const currentUser = this._authRepo.getUser();
          const currentUserId = currentUser?.person_id;
          const currentUserIsSupervisor = currentUser &&
            [UserRole.Supervisor, UserRole.SuperUser, UserRole.SysAdmin].includes(currentUser.role as UserRole);
          
          const convertedMessages: ChatMessage[] = (apiMessages.map((apiMsg) => {
            // This method is only ever called in the supervisor/admin context (viewing a student's chat).
            // All messages with sender_type='supervisor' are therefore from the current user ("me").
            const isFromMe = apiMsg.author_account_id
              ? Number(apiMsg.author_account_id) === Number(this._authRepo.getUser()?.user_id)
              : this._role === UserRole.Supervisor && apiMsg.sender_type === 'supervisor' &&
                Number(apiMsg.sender_id) === Number(this._authRepo.getUser()?.person_id);
            
            const sender: 'me' | 'other' = isFromMe ? 'me' : 'other';
            let messageText = apiMsg.message || apiMsg.subject || '';
            
            // Try to extract text from JSON comments format
            if (messageText.startsWith('{') && messageText.includes('"comments"')) {
              try {
                const parsed = JSON.parse(messageText);
                if (parsed?.comments && Array.isArray(parsed.comments)) {
                  messageText = parsed.comments.map((c: any) => c.message).filter(Boolean).join('\n');
                }
              } catch (e) {
                // Not valid JSON, use as-is
              }
            }
            
            messageText = this.cleanMessageHtml(messageText);
            const hasFiles = (apiMsg.attached_files?.length ?? 0) > 0;
            if (!messageText.trim() && !hasFiles) {
              return null; // Filter out messages with no content and no attachments
            }
            
            const senderName = isFromMe ? 'You' : (apiMsg.sender?.name || this.selectedStudent?.name || 'Student');
            const rawSenderAvatar = isFromMe
              ? this._resolveAvatarUrl(currentUser?.profile_image)
              : (this._resolveAvatarUrl(apiMsg.sender?.avatar_url) || this._resolveStudentAvatarUrl(this.selectedStudent) || null);
            const senderAvatar = rawSenderAvatar && !rawSenderAvatar.includes('assets/avatar.png') && !rawSenderAvatar.includes('assets/avatar-blank.png') ? rawSenderAvatar : null;

            // Capture twin info for the header if this is a twin message
            if (!isFromMe && apiMsg.sender_type === 'twin' && !this.twinInfo) {
              this.twinInfo = { name: senderName, avatar: senderAvatar };
            }
            
            const messageDate = new Date(apiMsg.created);
            
            return {
              sender,
              text: messageText,
              timestamp: messageDate.getTime(),
              senderName,
              senderRole: apiMsg.sender_type || 'student',
              senderAvatar,
              date: messageDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
              time: messageDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
              status: (apiMsg.status_id === 2 ? 'read' : 'delivered') as 'sent' | 'delivered' | 'read',
              messageId: apiMsg.t_ot_messagesid,
              stepId: apiMsg.student_course_step_id || null,
              stepTitle: apiMsg.step_title || apiMsg.subject || '',
              stepSort: apiMsg.step_sort || null,
              actiontypeId: apiMsg.actiontype_id || null,
              courseTitle: apiMsg.course_title || '',
              studentCourseId: apiMsg.student_course_id || null,
              isNotification: apiMsg.is_notification === 1,
              attentionState: apiMsg.attention_state || '',
              ackRequired: Number(apiMsg.ack_required || 0) === 1,
              ackAt: apiMsg.ack_at || null,
              deliveredAt: apiMsg.delivered_at || apiMsg.created || null,
              readAt: apiMsg.read_at || null,
              attachedFiles: (apiMsg.attached_files || []).map(f => ({ ...f, uuid: f.uuid ?? '', is_image: !!f.is_image })),
            } as ChatMessage;
          }) as Array<ChatMessage | null>).filter((m): m is ChatMessage => m !== null);
          
          convertedMessages.sort((a, b) => a.timestamp - b.timestamp);
          this.chatMessages = convertedMessages;
          this.chatTotalMessages = total;
          this.chatHasMore = convertedMessages.length < total;
          this.scrollToBottomAdmin();
        },
        error: (error) => {
          console.error('Error loading student chat messages:', error);
          this.chatMessages = [];
          this.chatHasMore = false;
        }
      });
  }

  /**
   * Load more (older) chat messages for the selected student (supervisor view)
   */
  loadMoreChatMessages(): void {
    const studentId = this.selectedStudent?.t_ot_studentid;
    if (!studentId || this.chatIsLoadingMore || !this.chatHasMore) return;

    const el = this.adminMessagesContainer?.nativeElement;
    const oldScrollHeight = el?.scrollHeight ?? 0;
    const oldScrollTop = el?.scrollTop ?? 0;

    this.chatIsLoadingMore = true;
    this.chatPage += 1;

    this._chatService.getMessagesPage(studentId, this.chatPage, this.chatPerPage)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: ({ messages: apiMessages, total }) => {
          this.chatIsLoadingMore = false;
          if (!apiMessages || apiMessages.length === 0) {
            this.chatHasMore = false;
            return;
          }

          const currentUser = this._authRepo.getUser();
          const currentUserId = currentUser?.person_id;
          const currentUserIsSupervisorMore = currentUser &&
            [UserRole.Supervisor, UserRole.SuperUser, UserRole.SysAdmin].includes(currentUser.role as UserRole);

          const olderConverted: ChatMessage[] = (apiMessages.map((apiMsg) => {
            // Same rule as loadStudentChatMessages: supervisor context only, all supervisor-type = 'me'.
            const isFromMe = apiMsg.author_account_id
              ? Number(apiMsg.author_account_id) === Number(this._authRepo.getUser()?.user_id)
              : this._role === UserRole.Supervisor && apiMsg.sender_type === 'supervisor' &&
                Number(apiMsg.sender_id) === Number(this._authRepo.getUser()?.person_id);
            const sender: 'me' | 'other' = isFromMe ? 'me' : 'other';
            let messageText = apiMsg.message || apiMsg.subject || '';
            if (messageText.startsWith('{') && messageText.includes('"comments"')) {
              try {
                const parsed = JSON.parse(messageText);
                if (parsed?.comments && Array.isArray(parsed.comments)) {
                  messageText = parsed.comments.map((c: any) => c.message).filter(Boolean).join('\n');
                }
              } catch {
                // use as-is
              }
            }
            messageText = this.cleanMessageHtml(messageText);
            const hasFilesMore = (apiMsg.attached_files?.length ?? 0) > 0;
            if (!messageText.trim() && !hasFilesMore) return null;
            const senderName = isFromMe ? 'You' : (apiMsg.sender?.name || this.selectedStudent?.name || 'Student');
            const rawSenderAvatarMore = isFromMe
              ? this._resolveAvatarUrl(currentUser?.profile_image)
              : (this._resolveAvatarUrl(apiMsg.sender?.avatar_url) || this._resolveStudentAvatarUrl(this.selectedStudent) || null);
            const senderAvatar = rawSenderAvatarMore && !rawSenderAvatarMore.includes('assets/avatar.png') && !rawSenderAvatarMore.includes('assets/avatar-blank.png') ? rawSenderAvatarMore : null;
            // Capture twin info for the header if this is a twin message
            if (!isFromMe && apiMsg.sender_type === 'twin' && !this.twinInfo) {
              this.twinInfo = { name: senderName, avatar: senderAvatar };
            }
            const messageDate = new Date(apiMsg.created);
            return {
              sender,
              text: messageText,
              timestamp: messageDate.getTime(),
              senderName,
              senderRole: apiMsg.sender_type || 'student',
              senderAvatar,
              date: messageDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
              time: messageDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
              status: (apiMsg.status_id === 2 ? 'read' : 'delivered') as 'sent' | 'delivered' | 'read',
              messageId: apiMsg.t_ot_messagesid,
              stepId: apiMsg.student_course_step_id || null,
              stepTitle: apiMsg.step_title || apiMsg.subject || '',
              stepSort: apiMsg.step_sort || null,
              actiontypeId: apiMsg.actiontype_id || null,
              courseTitle: apiMsg.course_title || '',
              studentCourseId: apiMsg.student_course_id || null,
              isNotification: apiMsg.is_notification === 1,
              attentionState: apiMsg.attention_state || '',
              ackRequired: Number(apiMsg.ack_required || 0) === 1,
              ackAt: apiMsg.ack_at || null,
              deliveredAt: apiMsg.delivered_at || apiMsg.created || null,
              readAt: apiMsg.read_at || null,
              attachedFiles: (apiMsg.attached_files || []).map(f => ({ ...f, uuid: f.uuid ?? '', is_image: !!f.is_image })),
            } as ChatMessage;
          }) as Array<ChatMessage | null>).filter((m): m is ChatMessage => m !== null);

          olderConverted.sort((a, b) => a.timestamp - b.timestamp);
          const totalLoaded = this.chatMessages.length + olderConverted.length;
          this.chatMessages = [...olderConverted, ...this.chatMessages];
          this.chatHasMore = totalLoaded < total;

          // Preserve scroll position after prepend
          setTimeout(() => {
            if (el) {
              const newScrollHeight = el.scrollHeight;
              el.scrollTop = oldScrollTop + (newScrollHeight - oldScrollHeight);
            }
          }, 0);
        },
        error: () => {
          this.chatIsLoadingMore = false;
        },
      });
  }

  /**
   * Go back to students list (for admins/supervisors)
   */
  backToStudentsList(): void {
    this.selectedStudent = null;
    this.isInStudentChat = false;
  }

  // ---- Staff direct chat --------------------------------------------------

  /** Chat button badge: student-thread unread plus staff direct-chat unread. */
  get chatButtonBadge(): number {
    return this.totalUnreadMessages + (this.directChat?.unreadTotal || 0);
  }

  get activeChatCategory(): DirectChatCategoryId {
    return this.directChat?.activeCategory || 'students';
  }

  get chatHeaderSubtitleKey(): string {
    const category = this.activeChatCategory;
    return category === 'students' ? 'Chat with Students' : 'chat.category.' + category;
  }

  private _initDirectChat(): void {
    if (this._role === UserRole.Student) return;
    this.directChat = new DirectChatDrawer(this._directChat, this._role, {
      destroy$: this._unsubscribeAll,
      translate: (key) => this._translateService.instant(key),
      isChatVisible: () => !!this.chatDrawer?.opened && this.drawerMode === 'chat',
      messagesContainer: () => this.directMessagesContainer?.nativeElement,
    });
    this.directChat.init();
  }

  selectChatCategory(category: DirectChatCategoryId): void {
    this.directChat?.selectCategory(category);
    if (category === 'students') this.backToStudentsList();
  }

  getDirectAvatarUrl(recipient: DirectChatRecipient | null): string | null {
    return recipient ? this._resolveAvatarUrl(recipient.avatar) : null;
  }

  /**
   * Track student by function for ngFor
   */
  trackStudentByFn(index: number, student: Student): any {
    return student.t_ot_studentid || index;
  }

  // Action type labels for notification badges
  private static readonly STEP_TYPE_LABELS: { [key: number]: string } = {
    1: 'chat.stepType.watchVideo', 2: 'chat.stepType.readText', 3: 'chat.stepType.readPdf', 4: 'chat.stepType.writeEssay',
    5: 'chat.stepType.doDrill', 6: 'chat.stepType.checkout', 7: 'chat.stepType.courseStart', 8: 'chat.stepType.completion',
    9: 'chat.stepType.drillSkype', 10: 'chat.stepType.clearWord', 11: 'chat.stepType.readPolicy', 12: 'chat.stepType.youtube',
    13: 'chat.stepType.quiz', 14: 'chat.stepType.finalTest', 15: 'chat.stepType.howAreYou', 16: 'chat.stepType.twinning',
    17: 'chat.stepType.supervisor', 18: 'chat.stepType.gallery', 19: 'chat.stepType.badge', 20: 'chat.stepType.startNewCourse',
    21: 'chat.stepType.phoneticWord', 22: 'chat.stepType.videoSlides',
  };

  /**
   * Get the human-readable label for a step action type
   */
  getStepTypeLabel(actiontypeId: number | undefined): string {
    if (!actiontypeId) return '';
    const key = CompactLayoutComponent.STEP_TYPE_LABELS[actiontypeId];
    return key ? this._translateService.instant(key) : this._translateService.instant('chat.stepType.unknown', { id: actiontypeId });
  }

  getSenderRoleLabel(msg: ChatMessage): string {
    const role = (msg.senderRole || '').toLowerCase();
    if (role === 'supervisor') return 'chat.supervisor';
    if (role === 'twin') return 'chat.senderRole.twin';
    if (role === 'system') return 'System';
    return 'Student';
  }

  getShortStepLabel(msg: ChatMessage): string {
    if (msg.stepSort) {
      return this._translateService.instant('chat.stepNumber', { n: msg.stepSort });
    }
    return this._translateService.instant('Step');
  }

  getAttentionStateLabel(msg: ChatMessage): string {
    const state = (msg.attentionState || '').toLowerCase();
    if (state === 'needs_supervisor_action') return 'chat.state.needsSupervisorAction';
    if (state === 'waiting_for_student') return 'chat.state.waitingForStudent';
    if (state === 'new_message') return 'chat.state.newMessage';
    if (state === 'resolved') return 'chat.state.resolved';
    return 'chat.state.message';
  }

  getAttentionStateClass(msg: ChatMessage): string {
    const state = (msg.attentionState || '').toLowerCase();
    if (state === 'needs_supervisor_action') return 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-200';
    if (state === 'waiting_for_student') return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200';
    if (state === 'new_message') return 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200';
    return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
  }

  acknowledgeMessage(msg: ChatMessage): void {
    if (!msg.messageId || msg.ackAt) {
      return;
    }
    this._lifecycleLog('acknowledgeMessage() request', {
      messageId: msg.messageId,
      ackRequired: msg.ackRequired,
      ackAt: msg.ackAt,
      attentionState: msg.attentionState,
    }, '#d97706');
    this._chatService.markMessageAsRead(msg.messageId)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => {
        msg.ackAt = new Date().toISOString();
        msg.readAt = msg.readAt || msg.ackAt;
        msg.status = 'read';
        msg.attentionState = 'resolved';
        // Decrement the local unread count for this student so badges update immediately
        if (this.selectedStudent?.t_ot_studentid) {
          const sid = this.selectedStudent.t_ot_studentid;
          if ((this.unreadMessageCounts[sid] || 0) > 0) {
            this.unreadMessageCounts[sid]--;
          }
        }
        this.loadTotalUnreadCount();
        this._lifecycleLog('acknowledgeMessage() success', {
          messageId: msg.messageId,
          ackAt: msg.ackAt,
          readAt: msg.readAt,
          status: msg.status,
        }, '#16a34a');
      }, (err) => {
        this._lifecycleLog('acknowledgeMessage() failed', { messageId: msg.messageId, err }, '#dc2626');
      });
  }

  /**
   * Build a tooltip string for a notification chat message
   */
  buildNotificationTooltip(msg: ChatMessage): string {
    const parts: string[] = [];
    const t = (key: string, params: object): string => this._translateService.instant(key, params);
    if (msg.courseTitle) parts.push(t('notifications.tooltipCourse', { title: msg.courseTitle }));
    if (msg.stepSort) parts.push(t('chat.stepNumber', { n: msg.stepSort }));
    if (msg.stepTitle) parts.push(t('notifications.tooltipTitle', { title: msg.stepTitle }));
    if (msg.actiontypeId) {
      const label = this.getStepTypeLabel(msg.actiontypeId);
      if (label) parts.push(t('notifications.tooltipType', { type: label }));
    }
    return parts.join('\n');
  }

  /**
   * Navigate to a specific student course step from a notification badge click
   */
  private static readonly STEP_TYPE_ROUTES: { [key: number]: string } = {
    1: 'watch-video', 2: 'read-text', 3: 'read-pdf', 4: 'write-essay',
    5: 'do-drill', 6: 'checkout', 7: 'course-start', 8: 'completion',
    9: 'drill-skype', 10: 'clear-word', 11: 'read-policy', 12: 'youtube',
    13: 'quiz', 14: 'final-test', 15: 'how-are-you', 16: 'twinning',
    17: 'supervisor', 18: 'gallery',
  };

  /**
   * Supervisor: navigate to /supervisor/{student}/{course}/(secondary:{stepRoute};...) 
   * for the step that this chat message is associated with.
   */
  navigateToNotificationStep(msg: ChatMessage): void {
    const studentId = this.selectedStudent?.t_ot_studentid;
    if (!studentId) return;
    this.stepNavigation.open({ studentId, courseId: msg.studentCourseId, stepId: msg.stepId })
      .then(opened => { if (opened) this.closeDrawer(); });
  }

  /**
   * Student: navigate to /academy/{course}/(secondary:{stepRoute};...) 
   * for the step that this chat message is associated with.
   */
  navigateToChatStep(msg: ChatMessage): void {
    const studentId = this._authRepo.getUser()?.person_id;
    if (!studentId) return;
    this.stepNavigation.open({ studentId, courseId: msg.studentCourseId, stepId: msg.stepId })
      .then(opened => { if (opened) this.closeDrawer(); });
  }

  /**
   * Load students list for admin/supervisor chat
   */
  private loadStudentsList(): void {
    if (this._role !== UserRole.Student && this._role !== UserRole.Translator) {
      const companyId = this._companiesService.getSelectedCompanyId();
      this._studentsService.fetchStudents(100, companyId)
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe((students) => {
          this.studentsList = students;
          this.filteredStudentsList = [...this.studentsList]; // Initialize filtered list
          this.initializeUnreadCounts(); // Initialize unread counts
          this.buildSupervisorCourseMap(); // Build student_id -> course_id for Review button
        });
    }
  }

  private buildSupervisorCourseMap(): void {
    this._supervisorService.refreshPendingStepsSummary()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe();
  }

  /**
   * Navigate to supervisor activity list from top-nav review icon.
   */
  navigateToSupervisorPage(): void {
    this._router.navigate(['/supervisor']);
  }

  /**
   * Navigate to supervisor review for given student; optionally close chat drawer.
   */
  navigateToSupervisorReview(student: Student): void {
    const courseId = this.supervisorCourseMap[student.t_ot_studentid];
    if (courseId) {
      this._router.navigate(['/supervisor', student.t_ot_studentid, courseId]);
    } else {
      this._router.navigate(['/supervisor']);
    }
    this.closeDrawer();
  }

  /**
   * Initialize unread message counts from real API
   */
  private initializeUnreadCounts(): void {
    this._supervisorService.getCanonicalCounters()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((counters) => {
        this.unreadMessageCounts = {};
        this.unreadNotificationCounts = {};
        Object.entries(counters?.by_student || {}).forEach(([studentIdStr, value]) => {
          const studentId = Number(studentIdStr);
          if (!studentId) return;
          this.unreadMessageCounts[studentId] = Number(value?.messages_unread || 0);
          this.unreadNotificationCounts[studentId] = Number(value?.notifications_needs_action || 0);
        });
        this._lifecycleLog('initializeUnreadCounts()', {
          students: Object.keys(counters?.by_student || {}).length,
          totals: counters?.totals || {},
        }, '#0891b2');
      });
  }

  /**
   * Get unread message count for a student
   */
  getUnreadCount(studentId: number): number {
    return (this.unreadMessageCounts[studentId] || 0) + (this.unreadNotificationCounts[studentId] || 0);
  }

  /**
   * Actionable system notifications for a student (matches bell badge semantics).
   */
  getUnreadNotificationCount(studentId: number): number {
    return this.unreadNotificationCounts[studentId] || 0;
  }

  /**
   * Pending step reviews for a student (supervisor activity list semantics).
   */
  getPendingStepCount(studentId: number): number {
    return this.supervisorPendingCountMap[studentId] || 0;
  }

  private _refreshCompanyScopedNotificationCount(): void {
    if (!this._hasCounterAccess) {
      return;
    }

    const companyId = this._companiesService.getSelectedCompanyId();
    this._notificationsService.getUnreadCount(companyId)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((count) => {
        this.totalUnreadNotifications = count;
        this.totalUnreadCount = count;
        this.updateNavigationBadge();
      });
  }

  /**
   * Clear unread messages for a student (when opening chat)
   * Marks messages as read via API and clears local count
   */
  clearUnreadCount(studentId: number): void {
    if (this.unreadMessageCounts[studentId]) {
      const before = {
        messages: this.unreadMessageCounts[studentId] || 0,
        notifications: this.unreadNotificationCounts[studentId] || 0,
      };
      this.unreadMessageCounts[studentId] = 0;
      this.unreadNotificationCounts[studentId] = this.unreadNotificationCounts[studentId] || 0;
      // Mark as read on the server
      this._chatService.markAllAsRead(studentId)
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe(() => {
          this._lifecycleLog('clearUnreadCount() markAllAsRead success', {
            studentId,
            before,
            after: {
              messages: this.unreadMessageCounts[studentId] || 0,
              notifications: this.unreadNotificationCounts[studentId] || 0,
            },
          }, '#16a34a');
        }, (err) => {
          this._lifecycleLog('clearUnreadCount() markAllAsRead failed', { studentId, err }, '#dc2626');
        });
    }
  }

  /**
   * Filter students based on search term
   */
  filterStudents(searchTerm: string): void {
    this.searchTerm = searchTerm;
    if (!searchTerm || searchTerm.trim() === '') {
      this.filteredStudentsList = [...this.studentsList];
    } else {
      const term = searchTerm.toLowerCase().trim();
      this.filteredStudentsList = this.studentsList.filter(student => 
        student.name?.toLowerCase().includes(term) ||
        student.email?.toLowerCase().includes(term) ||
        student.company?.toLowerCase().includes(term)
      );
    }
  }

  gotoClassroomAdmin() {
    this._router.navigate(['/vclass-admin']);
  }

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    // Get the navigation
    const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }

  /**
   * Handle company selection change from the top nav company selector
   * This triggers a reload of data in list components that depend on company context
   */
  onCompanyChanged(companyId: number | null): void {
    console.log('Company changed to:', companyId);
    // The CompaniesService already handles persistence and state updates
    // List components should subscribe to selectedCompanyId$ and reload their data
  }

  toggleTwinning() {
    console.log(this._authRepo.getUser().isactivated);
    +this._authRepo.getUser().isactivated == 1 ? (this._authRepo.getUser().isactivated = 0) : (this._authRepo.getUser().isactivated = 1);
  }

  goHome(): void {
    this._router.navigate(['/home']);
  }

  /**
   * Load total unread message count for supervisor
   * Separates chat messages (green) from notifications (orange)
   */
  private loadTotalUnreadCount(): void {
    if (this._hasCounterAccess) {
      this._lifecycleLog('loadTotalUnreadCount() start', { role: this._role }, '#0284c7');
      this._supervisorService.getCanonicalCounters()
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe((counters) => {
          const before = {
            totalUnreadCount: this.totalUnreadCount,
            chatBadgeCount: this.chatBadgeCount,
            totalUnreadMessages: this.totalUnreadMessages,
            totalUnreadNotifications: this.totalUnreadNotifications,
            totalWaitingForStudent: this.totalWaitingForStudent,
          };
          const totalMsgs = Number(counters?.totals?.messages_unread || 0);
          const waiting = Number(counters?.totals?.waiting_for_student || 0);
          this.totalUnreadMessages = totalMsgs;
          this.totalWaitingForStudent = waiting;
          this.chatBadgeCount = totalMsgs; // Chat button shows only chat messages
          this._lifecycleLog('Unread counters loaded', {
            before,
            after: {
              totalUnreadCount: this.totalUnreadCount,
              chatBadgeCount: this.chatBadgeCount,
              totalUnreadMessages: this.totalUnreadMessages,
              totalUnreadNotifications: this.totalUnreadNotifications,
              totalWaitingForStudent: this.totalWaitingForStudent,
            },
            totals: { totalMsgs, waiting },
          }, '#0f766e');
          this._refreshCompanyScopedNotificationCount();
        }, (err) => {
          this._lifecycleLog('loadTotalUnreadCount() failed', err, '#dc2626');
        });
    }
  }

  /**
   * Load total unread message count for student (from current course)
   */
  private loadStudentUnreadCount(): void {
    if (this._role !== UserRole.Student || document.hidden) return;
    this.messageCounters.getCounters().pipe(takeUntil(this._unsubscribeAll)).subscribe(counters => {
      const total = counters.totals.messages_unread;
      this.totalUnreadCount = total;
      this.chatBadgeCount = total;
      this.totalUnreadMessages = total;
      this._changeDetectorRef.markForCheck();
    });
  }

  /**
   * Update navigation items with badge for Messages item
   */
  private updateNavigationBadge(): void {
    // Only update badge for supervisors and admin roles
    if (!this._hasCounterAccess) {
      return;
    }
    
    if (this.navigation && this.navigation.admin) {
      const messagesItem = this.navigation.admin.find(item => item.id === 'supervisor');
        if (messagesItem) {
        if (this.totalPendingStepsCount > 0) {
          messagesItem.badge = {
            title: this.totalPendingStepsCount.toString(),
            classes: 'px-2 bg-orange-500 text-white rounded-full'
          };
          this._lifecycleLog('Navigation badge updated', {
            badge: messagesItem.badge,
            count: this.totalPendingStepsCount,
          }, '#f97316');
        } else {
          messagesItem.badge = undefined;
          this._lifecycleLog('Navigation badge cleared', { count: this.totalPendingStepsCount }, '#f97316');
        }
        // Create a new navigation array to trigger change detection
        this.navigation = {
          ...this.navigation,
          admin: this.navigation.admin.map(item => {
            if (item.id === 'supervisor') {
              return { ...item, badge: messagesItem.badge };
            }
            return item;
          })
        };
      } else {
        console.warn('Messages navigation item not found in admin navigation');
      }
    }
  }

  /** A quiet poll is already in flight; another must not stack on it. */
  private chatRefreshInFlight = false;

  /**
   * Poll path: fetch the latest messages without replacing the list.
   *
   * Messages already on screen are updated in place. Only ones that are not
   * there yet are appended. The view scrolls only when a new message arrived
   * while the student was already at the bottom.
   */
  private refreshChatMessages(): void {
    if (this._role !== UserRole.Student || this.chatRefreshInFlight) {
      return;
    }

    const studentId = this._authRepo.getUser()?.person_id;
    if (!studentId) {
      return;
    }

    this.chatRefreshInFlight = true;
    this._chatService.getMessages(studentId, 1, 20)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (apiMessages: ApiChatMessage[]) => {
          this.chatRefreshInFlight = false;
          if (!apiMessages || apiMessages.length === 0) {
            return;
          }
          this.processApiMessages(apiMessages, studentId, 'bottom-if-new');
          this.markUnreadIfAckDisabled(apiMessages, studentId);
        },
        error: () => {
          this.chatRefreshInFlight = false;
        },
      });
  }

  /**
   * Load chat messages from API for the current student
   */
  private loadChatMessages(): void {
    if (this._role !== UserRole.Student) {
      return;
    }

    const studentId = this._authRepo.getUser()?.person_id;
    if (!studentId) {
      console.warn('Cannot load chat messages: student ID not found');
      return;
    }

    // Try to detect current step from URL
    this.detectCurrentStepFromUrl();
    
    this._lifecycleLog('loadChatMessages() start', {
      studentId,
      currentStepId: this.currentStepId,
      route: this._router.url,
    }, '#7c3aed');

    // Clear existing messages first (including mock messages)
    this.chatMessages = [];
    this.groupedMessages = [];
    const loginUser = this._authRepo.getUser();
    this.supervisorInfo = loginUser?.supervisor_name
      ? { name: loginUser.supervisor_name, avatar: this._resolveAvatarUrl(loginUser.supervisor_avatar) }
      : null;

    // Load last 20 messages from API
    this._chatService.getMessages(studentId, 1, 20)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (apiMessages: ApiChatMessage[]) => {
          this._lifecycleLog('messages API loaded', {
            count: apiMessages.length,
            sampleIds: (apiMessages || []).slice(0, 5).map(m => m?.t_ot_messagesid),
          }, '#8b5cf6');
          
          if (!apiMessages || apiMessages.length === 0) {
            // No messages from API, keep empty array
            this._lifecycleLog('messages API empty', { studentId }, '#a855f7');
            this.chatMessages = [];
            this.saveMessagesToStorage();
            return;
          }
          
          this.processApiMessages(apiMessages, studentId);
          this.markUnreadIfAckDisabled(apiMessages, studentId);
        },
        error: (error) => {
          this._lifecycleLog('loadChatMessages() failed', {
            message: error,
            status: error?.status,
            error: error?.error
          }, '#dc2626');
          
          // If it's a JSON parse error, try to extract messages from error.error if it's a string
          if (error?.error && typeof error.error === 'string' && error.error.includes('{')) {
            try {
              const jsonStart = error.error.indexOf('{');
              const jsonEnd = error.error.lastIndexOf('}');
              if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
                const jsonString = error.error.substring(jsonStart, jsonEnd + 1);
                const parsed = JSON.parse(jsonString);
                if (parsed && parsed.data && Array.isArray(parsed.data)) {
                  this._lifecycleLog('recovered messages from error payload', { count: parsed.data.length }, '#16a34a');
                  // Process the extracted messages
                  this.processApiMessages(parsed.data, studentId);
                  return; // Exit early, messages were processed
                }
              }
            } catch (parseErr) {
              this._lifecycleLog('failed parsing error payload', parseErr, '#dc2626');
            }
          }
          
          // Clear messages on error (don't show mock/old messages)
          this.chatMessages = [];
          this.saveMessagesToStorage();
        }
      });
  }

  /**
   * Companies without read-receipts still need the badge to clear once the
   * student has the conversation open.
   */
  private markUnreadIfAckDisabled(apiMessages: ApiChatMessage[], studentId: number): void {
    if (this.companyAckEnabled) {
      return;
    }
    const hasUnread = apiMessages.some(m => (m.status_id ?? 0) < 2 && m.sender_type !== 'student');
    if (!hasUnread) {
      return;
    }
    this._chatService.markAllAsRead(studentId)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: () => {
          const nowIso = new Date().toISOString();
          for (const message of this.chatMessages) {
            if (message.sender === 'me' || message.status === 'read') {
              continue;
            }
            message.status = 'read';
            message.readAt = message.readAt || nowIso;
          }
          this.loadTotalUnreadCount();
        }
      });
  }

  trackChatMessage(_index: number, msg: ChatMessage): string {
    return `${msg.messageId ?? 'x'}:${msg.timestamp}`;
  }

  trackMessageGroup(_index: number, group: MessageGroup): string {
    return String(group.stepId ?? 'general');
  }

  private chatMessageKey(message: ChatMessage): string {
    return `${message.messageId ?? ''}:${message.timestamp}`;
  }

  /** Copy delivery fields onto the row already on screen. The row itself stays. */
  private patchChatMessage(existing: ChatMessage, next: ChatMessage): void {
    if (existing.status !== next.status) existing.status = next.status;
    if (existing.readAt !== next.readAt) existing.readAt = next.readAt;
    if (existing.ackAt !== next.ackAt) existing.ackAt = next.ackAt;
    if (existing.deliveredAt !== next.deliveredAt) existing.deliveredAt = next.deliveredAt;
    if (existing.text !== next.text) existing.text = next.text;
    if (existing.ackRequired !== next.ackRequired) existing.ackRequired = next.ackRequired;
    if (!existing.senderAvatar && next.senderAvatar) existing.senderAvatar = next.senderAvatar;
  }

  /** Hang a new message on the step group it belongs to, creating that group if needed. */
  private appendMessageToGroup(message: ChatMessage): void {
    const stepId = message.stepId || null;
    let group = this.groupedMessages.find(item => (item.stepId || null) === stepId);
    if (!group) {
      group = {
        stepId,
        stepTitle: message.stepTitle || this._translateService.instant('chat.generalMessages'),
        courseTitle: message.courseTitle || '',
        studentCourseId: message.studentCourseId || null,
        messages: [],
      };
      this.groupedMessages = [...this.groupedMessages, group].sort((a, b) => {
        const first = (item: MessageGroup) =>
          item.messages[0]?.timestamp ?? (item === group ? message.timestamp : 0);
        return first(a) - first(b);
      });
    }
    group.messages = [...group.messages, message].sort((a, b) => a.timestamp - b.timestamp);
  }

  /**
   * Fold a poll into the open conversation.
   *
   * Returns how many messages were added. Existing rows are patched where
   * they sit, so the list is never cleared and rebuilt.
   */
  private mergePolledMessages(incoming: ChatMessage[]): number {
    const index = new Map(this.chatMessages.map(message => [this.chatMessageKey(message), message]));
    let added = 0;
    for (const next of incoming) {
      const existing = index.get(this.chatMessageKey(next));
      if (existing) {
        this.patchChatMessage(existing, next);
        continue;
      }
      this.chatMessages = [...this.chatMessages, next];
      index.set(this.chatMessageKey(next), next);
      this.appendMessageToGroup(next);
      added++;
    }
    if (added > 0) {
      this.chatMessages = [...this.chatMessages].sort((a, b) => a.timestamp - b.timestamp);
      this.saveMessagesToStorage();
    }
    return added;
  }

  /**
   * Process API messages and convert them to component format.
   *
   * `step` is the first load: jump to the step the student is on.
   * `bottom-if-new` is the poll: patch rows in place, append only what is new,
   * and follow to the bottom only when that new message arrived there.
   */
  private processApiMessages(
    apiMessages: ApiChatMessage[],
    studentId: number,
    scroll: 'step' | 'bottom-if-new' = 'step'
  ): void {
    // Convert API messages to component format
    const convertedMessages: ChatMessage[] = [];
    
    apiMessages.forEach((apiMsg) => {
      // Determine if message is from current user (student) or supervisor.
      // Old messages may have sender_id=null; fall back to sender_type alone in that case.
      // Use Number() coercion: PHP/MySQL may return sender_id as a string, causing strict === to fail.
      const isFromMe = apiMsg.sender_type === 'student' && (
        apiMsg.sender_id === null ||
        apiMsg.sender_id === undefined ||
        Number(apiMsg.sender_id) === Number(studentId)
      );
      
      const sender: 'me' | 'other' = isFromMe ? 'me' : 'other';
      
      // Use message content, or fallback to subject if message is empty
      let messageText = apiMsg.message || '';
      if (!messageText || messageText.trim().length === 0) {
        // If message is empty, use subject as fallback (for system notifications)
        messageText = apiMsg.subject || '';
      }
      
      // Clean up subject if it's just "[" (malformed)
      if (messageText === '[' || messageText.trim() === '[') {
        messageText = this._translateService.instant('chat.newStepRequiresApproval');
      }
      
      // Check if the message is a JSON with comments array (from phuture-comments)
      if (messageText.startsWith('{') && messageText.includes('"comments"')) {
        try {
          const parsed = JSON.parse(messageText);
          if (parsed && parsed.comments && Array.isArray(parsed.comments)) {
            // Process each comment as a separate message
            parsed.comments.forEach((comment: any) => {
              const commentDate = new Date(comment.timestamp);
              const dateStr = commentDate.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                year: commentDate.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined 
              });
              const timeStr = commentDate.toLocaleTimeString('en-US', { 
                hour: '2-digit', 
                minute: '2-digit',
                hour12: false 
              });
              
              // Determine if this comment is from the current user
              // Use Number() coercion: JSON-parsed sender_id may be a string if stored by the phuture-comments widget.
              const isCommentFromMe = Number(comment.sender_id) === Number(studentId);
              
              // Clean and decode HTML content
              let commentMessage = comment.message || '';
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = commentMessage;
              commentMessage = this.cleanMessageHtml(tempDiv.innerHTML);
              
              // Validate avatar URL - only discard known placeholder filenames
              let avatarUrl: string | null = null;
              const rawAvatar = comment.avatar || '';
              if (rawAvatar && !rawAvatar.includes('assets/avatar.png') && !rawAvatar.includes('assets/avatar-blank.png') && rawAvatar !== '') {
                avatarUrl = rawAvatar;
              }
              // For comments, prefer profile image of the actual user over widget-embedded avatars
              if (!avatarUrl) {
                if (isCommentFromMe) {
                  avatarUrl = this._resolveAvatarUrl(this._authRepo.getUser()?.profile_image);
                } else {
                  avatarUrl = apiMsg.sender?.avatar_url || null;
                }
              }
              
              const convertedComment: ChatMessage = {
                sender: isCommentFromMe ? 'me' : 'other',
                text: commentMessage,
                timestamp: commentDate.getTime(),
                senderName: comment.sender_name || (isCommentFromMe ? 'You' : 'Other'),
                senderAvatar: avatarUrl,
                date: dateStr,
                time: timeStr,
                status: comment.isRead ? 'read' : (comment.isDelivered ? 'delivered' : 'sent'),
                messageId: apiMsg.t_ot_messagesid,
                avatarError: false,
                stepId: apiMsg.student_course_step_id || null,
                stepTitle: apiMsg.step_title || apiMsg.subject || this._translateService.instant('chat.general'),
                stepSort: apiMsg.step_sort || null,
                actiontypeId: apiMsg.actiontype_id || null,
                courseTitle: apiMsg.course_title || '',
                studentCourseId: apiMsg.student_course_id || null,
                ackRequired: Number(apiMsg.ack_required || 0) === 1,
                ackAt: apiMsg.ack_at || null,
                deliveredAt: apiMsg.delivered_at || apiMsg.created || null,
                readAt: apiMsg.read_at || null,
              };
              
              convertedMessages.push(convertedComment);
            });
            return; // Skip the default message processing
          }
        } catch (e) {
          // Not valid JSON, continue with normal processing
          this._lifecycleLog('message payload not comments-json', {
            messageId: apiMsg?.t_ot_messagesid,
          }, '#6b7280');
        }
      }
      
      // Clean empty paragraphs left by TinyMCE
      messageText = this.cleanMessageHtml(messageText);
      
      // If still empty after fallback, skip only if there are no attachments either
      const hasAttachments = (apiMsg.attached_files?.length ?? 0) > 0;
      if ((!messageText || messageText.trim().length === 0) && !hasAttachments) {
        return;
      }
      
      // Get sender name and avatar from API response
      let senderName = 'You';
      let senderAvatar: string | null = null;
      
      if (isFromMe) {
        senderName = 'You';
        senderAvatar = this._resolveAvatarUrl(this._authRepo.getUser()?.profile_image);
      } else {
        if (apiMsg.sender) {
          senderName = apiMsg.sender.name || 'Supervisor';
          senderAvatar = this._resolveAvatarUrl(apiMsg.sender.avatar_url) || null;
        } else {
          senderName = apiMsg.sender_type === 'supervisor' ? 'Supervisor' : 'Other';
        }
        // Capture supervisor info for the Ask Teacher header (student view)
        if (!this.supervisorInfo && apiMsg.sender_type === 'supervisor') {
          this.supervisorInfo = { name: senderName, avatar: senderAvatar };
        } else if (this.supervisorInfo && !this.supervisorInfo.avatar && senderAvatar && apiMsg.sender_type === 'supervisor') {
          this.supervisorInfo = { ...this.supervisorInfo, avatar: senderAvatar };
        }
      }
      
      // Format date and time
      const messageDate = new Date(apiMsg.created);
      const dateStr = messageDate.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: messageDate.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined 
      });
      const timeStr = messageDate.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      });
      
      // Determine message status (for now, assume delivered if status_id is 1, read if status_id is 2)
      let messageStatus: 'sent' | 'delivered' | 'read' = 'sent';
      if (apiMsg.status_id !== undefined && apiMsg.status_id !== null) {
        if (apiMsg.status_id === 1) {
          messageStatus = 'delivered';
        } else if (apiMsg.status_id === 2) {
          messageStatus = 'read';
        }
      }
      
      // Discard only known placeholder paths — keep valid absolute or relative profile URLs
      let validatedAvatar = senderAvatar;
      if (validatedAvatar && (validatedAvatar.includes('assets/avatar.png') || validatedAvatar.includes('assets/avatar-blank.png'))) {
        validatedAvatar = null;
      }
      
      const convertedMsg: ChatMessage = {
        sender: sender,
        text: messageText,
        timestamp: messageDate.getTime(),
        senderName: senderName,
        senderAvatar: validatedAvatar,
        date: dateStr,
        time: timeStr,
        status: messageStatus,
        messageId: apiMsg.t_ot_messagesid,
        avatarError: false,
        stepId: apiMsg.student_course_step_id || null,
        stepTitle: apiMsg.step_title || apiMsg.subject || this._translateService.instant('chat.general'),
        courseTitle: apiMsg.course_title || '',
        studentCourseId: apiMsg.student_course_id || null,
        ackRequired: Number(apiMsg.ack_required || 0) === 1,
        ackAt: apiMsg.ack_at || null,
        deliveredAt: apiMsg.delivered_at || apiMsg.created || null,
        readAt: apiMsg.read_at || null,
        attachedFiles: (apiMsg.attached_files || []).map(f => ({ ...f, uuid: f.uuid ?? '', is_image: !!f.is_image })),
      };
      
      this._lifecycleLog('message converted', {
        id: convertedMsg.messageId,
        sender: convertedMsg.sender,
        status: convertedMsg.status,
        stepId: convertedMsg.stepId,
      }, '#9333ea');
      convertedMessages.push(convertedMsg);
    });

    // Sort by timestamp (oldest first)
    convertedMessages.sort((a, b) => a.timestamp - b.timestamp);

    // Take only the last 50 messages (most recent) - increased for comments
    const lastMessages = convertedMessages.slice(-50);

    // A poll never replaces the conversation. It updates rows that are already
    // visible and appends only the ones that are not.
    if (scroll === 'bottom-if-new') {
      if (lastMessages.length === 0 || this.chatMessages.length === 0) {
        return;
      }
      const wasAtBottom = !this.showScrollToBottom;
      const added = this.mergePolledMessages(lastMessages);
      if (added > 0 && wasAtBottom) {
        this.scrollToBottom();
      }
      return;
    }

    // Replace existing messages with API messages
    this.chatMessages = lastMessages;
    
    // Group messages by step
    this.groupMessagesByStep();
    
    this._lifecycleLog('messages lifecycle final', {
      totalMessages: this.chatMessages.length,
      groupedBuckets: this.groupedMessages.length,
      currentStepId: this.currentStepId,
    }, '#16a34a');
    
    // Save to localStorage for offline access
    this.saveMessagesToStorage();

    // Scroll to current step or bottom after messages are loaded
    this.scrollToCurrentStepOrBottom();
  }

  /**
   * Group messages by step_id
   */
  private groupMessagesByStep(): void {
    const groupMap = new Map<number | null, MessageGroup>();
    
    this.chatMessages.forEach((msg) => {
      const stepId = msg.stepId || null;
      
      if (!groupMap.has(stepId)) {
        groupMap.set(stepId, {
          stepId: stepId,
          stepTitle: msg.stepTitle || this._translateService.instant('chat.generalMessages'),
          courseTitle: msg.courseTitle || '',
          studentCourseId: msg.studentCourseId || null,
          messages: []
        });
      }
      
      groupMap.get(stepId)!.messages.push(msg);
    });
    
    // Convert map to array and sort by the earliest message timestamp in each group
    this.groupedMessages = Array.from(groupMap.values()).sort((a, b) => {
      const aFirst = a.messages[0]?.timestamp || 0;
      const bFirst = b.messages[0]?.timestamp || 0;
      return aFirst - bFirst;
    });
  }

  /**
   * Scroll to the current step's messages or to bottom if no current step
   */
  private scrollToCurrentStepOrBottom(): void {
    // Use setTimeout to ensure DOM is updated
    setTimeout(() => {
      if (!this.messagesContainer?.nativeElement) return;
      
      // If we have a current step, try to scroll to it
      if (this.currentStepId) {
        const stepElement = document.getElementById(`step-group-${this.currentStepId}`);
        if (stepElement) {
          // Find the last message in this step group and scroll to it
          const lastMessage = stepElement.querySelector('.chat-message:last-child');
          if (lastMessage) {
            lastMessage.scrollIntoView({ behavior: 'auto', block: 'center' });
            console.log('📍 Scrolled to last message of step:', this.currentStepId);
            return;
          }
          // Fallback: scroll to the step group header
          stepElement.scrollIntoView({ behavior: 'auto', block: 'start' });
          console.log('📍 Scrolled to step group:', this.currentStepId);
          return;
        }
      }
      
      // Default: scroll to bottom
      this.messagesContainer.nativeElement.scrollTop = this.messagesContainer.nativeElement.scrollHeight;
      console.log('📍 Scrolled to bottom');
    }, 150);
  }

  /**
   * Scroll the messages container to the bottom
   */
  private scrollToBottom(): void {
    // Use setTimeout to ensure DOM is updated
    setTimeout(() => {
      if (this.messagesContainer?.nativeElement) {
        this.messagesContainer.nativeElement.scrollTop = this.messagesContainer.nativeElement.scrollHeight;
      }
    }, 100);
  }

  /**
   * Scroll the admin/supervisor chat messages container to the bottom
   */
  private scrollToBottomAdmin(): void {
    setTimeout(() => {
      if (this.adminMessagesContainer?.nativeElement) {
        this.adminMessagesContainer.nativeElement.scrollTop = this.adminMessagesContainer.nativeElement.scrollHeight;
      }
    }, 100);
  }

  /**
   * Public method to scroll to bottom (called from template)
   */
  scrollToBottomPublic(): void {
    if (this.messagesContainer?.nativeElement) {
      this.messagesContainer.nativeElement.scrollTo({
        top: this.messagesContainer.nativeElement.scrollHeight,
        behavior: 'smooth'
      });
    }
  }

  /**
   * Handle scroll event on messages container to show/hide scroll-to-bottom button
   */
  onMessagesScroll(): void {
    if (!this.messagesContainer?.nativeElement) return;
    
    const element = this.messagesContainer.nativeElement;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;
    
    // Show button if scrolled up more than 100px from the bottom
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    this.showScrollToBottom = distanceFromBottom > 100;
  }

  /**
   * Handle scroll event on the admin/supervisor messages container
   * Tracks whether the user is near the bottom to decide whether to auto-scroll on new messages
   */
  onAdminMessagesScroll(): void {
    if (!this.adminMessagesContainer?.nativeElement) return;
    const element = this.adminMessagesContainer.nativeElement;
    const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    this.showScrollToBottomAdmin = distanceFromBottom > 100;
  }

  /**
   * Navigate to a specific step in the checksheet
   * Navigates to the academy page with the step selected
   * Keeps the chat drawer open for reference
   */
  navigateToStep(stepId: number | null): void {
    if (!stepId) {
      console.warn('Cannot navigate: stepId is null');
      return;
    }
    
    // Find the message group to get student_course_id
    const targetGroup = this.groupedMessages.find(g => g.stepId === stepId);
    const messageStudentCourseId = targetGroup?.studentCourseId;
    
    // Check if we're on an academy page
    const url = this._router.url;
    const isOnAcademy = url.includes('/academy/');
    const courseIdMatch = url.match(/\/academy\/(\d+)/);
    const currentStudentCourseId = courseIdMatch ? parseInt(courseIdMatch[1], 10) : null;
    
    // Update current step for highlighting in chat
    this.currentStepId = stepId;
    
    console.log('🚀 Navigation check - isOnAcademy:', isOnAcademy, 'currentCourse:', currentStudentCourseId, 'messageCourse:', messageStudentCourseId);
    
    // Check if we're on the CORRECT course
    if (isOnAcademy && currentStudentCourseId && messageStudentCourseId && currentStudentCourseId === messageStudentCourseId) {
      // We're on the correct academy page - use the service to navigate within the page
      console.log('🚀 Same course - requesting navigation to step:', stepId);
      this._checksheetChangeService.requestNavigateToStep(stepId);
    } else if (messageStudentCourseId) {
      // Either not on academy page OR on a different course - navigate to the correct course
      console.log('🚀 Navigating to academy course:', messageStudentCourseId, 'step:', stepId);
      
      // Navigate to the academy with the step
      this._router.navigate(['/academy', messageStudentCourseId]).then(() => {
        // After navigation, request the step selection with a delay
        // to ensure the academy component is loaded and checksheet is fetched
        setTimeout(() => {
          console.log('🚀 Requesting step selection after navigation:', stepId);
          this._checksheetChangeService.requestNavigateToStep(stepId);
        }, 1500); // Increased delay to allow checksheet to load
      });
    } else {
      console.warn('Cannot navigate: no student_course_id available for step:', stepId);
    }
  }

  /**
   * Strip empty / whitespace-only paragraphs that TinyMCE leaves behind.
   * Removes <p>&nbsp;</p>, <p></p>, <p><br/></p> etc. from HTML content.
   */
  private cleanMessageHtml(html: string): string {
    if (!html) return html;
    return html
      .replace(/<p[^>]*>(\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, '')
      .trim();
  }

  /**
   * Append the JWT access token to a profile-image URL so the authenticated
   * image endpoint returns the image instead of a 401.
   * Mirrors the logic of ProfileImageUrlPipe for use inside TypeScript code.
   * Returns null when the URL is falsy (no image configured).
   */
  private _resolveAvatarUrl(url: string | null | undefined): string | null {
    if (!url) return null;
    if (url.startsWith('data:') || url.includes('?token=')) return url;
    const token = this._authRepo.getAccessToken();
    return token ? `${url}?token=${encodeURIComponent(token)}` : url;
  }

  /**
   * Build a proper authenticated avatar URL for a Student object.
   * Handles raw filenames (converts to API serve URL using account_id)
   * as well as already-complete URLs (just appends JWT token).
   */
  private _resolveStudentAvatarUrl(student: Student | null | undefined): string | null {
    if (!student) return null;
    const raw = student.profile_image as string | null | undefined;
    if (raw) {
      if (raw.startsWith('http') || raw.startsWith('/')) {
        return this._resolveAvatarUrl(raw);
      }
      if (student.account_id) {
        return this._resolveAvatarUrl(`${this.appConfig.get('api_baseurl')}/account/profile-image/${student.account_id}`);
      }
    }
    return this._resolveAvatarUrl(student.picture) || null;
  }

  /** Avatar URL for the currently selected student (chat header + message list). */
  get selectedStudentAvatarUrl(): string | null {
    return this._resolveStudentAvatarUrl(this.selectedStudent);
  }

  /** Avatar URL for the currently logged-in user (supervisor/admin/etc.). */
  get currentUserAvatarUrl(): string | null {
    return this._resolveAvatarUrl(this._authRepo.getUser()?.profile_image);
  }

  /** Display name of the currently logged-in user. */
  get currentUserDisplayName(): string {
    return this._authRepo.getUser()?.name || '';
  }

  /** Avatar URL for any student in the inline student list. */
  getStudentListAvatarUrl(student: Student): string | null {
    return this._resolveStudentAvatarUrl(student);
  }

  /**
   * Detect current step from URL parameters
   * Looks for step_id in secondary outlet or query params
   */
  private detectCurrentStepFromUrl(): void {
    const url = this._router.url;
    
    // Try to get step_id from URL patterns:
    // /academy/123/(secondary:write-essay;scid=123;step_id=456)
    // or step_id=123 in query params
    
    let stepId: number | null = null;
    
    // Pattern 1: step_id in secondary outlet params
    const stepIdMatch = url.match(/step_id[=:](\d+)/i);
    if (stepIdMatch) {
      stepId = parseInt(stepIdMatch[1], 10);
    }
    
    // Pattern 2: Try to get from router state
    if (!stepId) {
      const routerState = this._router.routerState;
      let childRoute = routerState.root.firstChild;
      while (childRoute) {
        // Check for secondary outlet
        const secondaryOutlet = childRoute.children.find(
          child => child.outlet === 'secondary'
        );
        
        if (secondaryOutlet) {
          const stepIdParam = secondaryOutlet.snapshot.paramMap.get('step_id');
          if (stepIdParam) {
            stepId = parseInt(stepIdParam, 10);
            break;
          }
        }
        
        // Also check current route params
        const stepIdInRoute = childRoute.snapshot.paramMap.get('step_id');
        if (stepIdInRoute) {
          stepId = parseInt(stepIdInRoute, 10);
          break;
        }
        
        childRoute = childRoute.firstChild;
      }
    }
    
    this.currentStepId = stepId && !isNaN(stepId) ? stepId : null;
    console.log('🔍 Detected current step from URL:', this.currentStepId);
  }
}

